import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import LazySection from '@/components/base/LazySection';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { refreshUserFromDB } from '@/lib/customAuth';
import { hashPassword, verifyPassword, hashPasswordNoSalt } from '@/lib/hashUtils';
import PayPasswordModal from './components/PayPasswordModal';
import SuccessScreen from './components/SuccessScreen';

// 省市区数据
const PROVINCES = [
  { name: '北京市', cities: ['北京市'] },
  { name: '天津市', cities: ['天津市'] },
  { name: '上海市', cities: ['上海市'] },
  { name: '重庆市', cities: ['重庆市'] },
  { name: '河北省', cities: ['石家庄市', '唐山市', '秦皇岛市', '邯郸市', '邢台市', '保定市', '张家口市', '承德市', '沧州市', '廊坊市', '衡水市'] },
  { name: '山西省', cities: ['太原市', '大同市', '阳泉市', '长治市', '晋城市', '朔州市', '晋中市', '运城市', '忻州市', '临汾市', '吕梁市'] },
  { name: '辽宁省', cities: ['沈阳市', '大连市', '鞍山市', '抚顺市', '本溪市', '丹东市', '锦州市', '营口市', '阜新市', '辽阳市', '盘锦市', '铁岭市', '朝阳市', '葫芦岛市'] },
  { name: '吉林省', cities: ['长春市', '吉林市', '四平市', '辽源市', '通化市', '白山市', '松原市', '白城市', '延边朝鲜族自治州'] },
  { name: '黑龙江省', cities: ['哈尔滨市', '齐齐哈尔市', '鸡西市', '鹤岗市', '双鸭山市', '大庆市', '伊春市', '佳木斯市', '七台河市', '牡丹江市', '黑河市', '绥化市', '大兴安岭地区'] },
  { name: '江苏省', cities: ['南京市', '无锡市', '徐州市', '常州市', '苏州市', '南通市', '连云港市', '淮安市', '盐城市', '扬州市', '镇江市', '泰州市', '宿迁市'] },
  { name: '浙江省', cities: ['杭州市', '宁波市', '温州市', '嘉兴市', '湖州市', '绍兴市', '金华市', '衢州市', '舟山市', '台州市', '丽水市'] },
  { name: '安徽省', cities: ['合肥市', '芜湖市', '蚌埠市', '淮南市', '马鞍山市', '淮北市', '铜陵市', '安庆市', '黄山市', '滁州市', '阜阳市', '宿州市', '六安市', '亳州市', '池州市', '宣城市'] },
  { name: '福建省', cities: ['福州市', '厦门市', '莆田市', '三明市', '泉州市', '漳州市', '南平市', '龙岩市', '宁德市'] },
  { name: '江西省', cities: ['南昌市', '景德镇市', '萍乡市', '九江市', '新余市', '鹰潭市', '赣州市', '吉安市', '宜春市', '抚州市', '上饶市'] },
  { name: '山东省', cities: ['济南市', '青岛市', '淄博市', '枣庄市', '东营市', '烟台市', '潍坊市', '济宁市', '泰安市', '威海市', '日照市', '临沂市', '德州市', '聊城市', '滨州市', '菏泽市'] },
  { name: '河南省', cities: ['郑州市', '开封市', '洛阳市', '平顶山市', '安阳市', '鹤壁市', '新乡市', '焦作市', '濮阳市', '许昌市', '漯河市', '三门峡市', '南阳市', '商丘市', '信阳市', '周口市', '驻马店市'] },
  { name: '湖北省', cities: ['武汉市', '黄石市', '十堰市', '宜昌市', '襄阳市', '鄂州市', '荆门市', '孝感市', '荆州市', '黄冈市', '咸宁市', '随州市', '恩施土家族苗族自治州'] },
  { name: '湖南省', cities: ['长沙市', '株洲市', '湘潭市', '衡阳市', '邵阳市', '岳阳市', '常德市', '张家界市', '益阳市', '郴州市', '永州市', '怀化市', '娄底市', '湘西土家族苗族自治州'] },
  { name: '广东省', cities: ['广州市', '韶关市', '深圳市', '珠海市', '汕头市', '佛山市', '江门市', '湛江市', '茂名市', '肇庆市', '惠州市', '梅州市', '汕尾市', '河源市', '阳江市', '清远市', '东莞市', '中山市', '潮州市', '揭阳市', '云浮市'] },
  { name: '海南省', cities: ['海口市', '三亚市', '三沙市', '儋州市'] },
  { name: '四川省', cities: ['成都市', '自贡市', '攀枝花市', '泸州市', '德阳市', '绵阳市', '广元市', '遂宁市', '内江市', '乐山市', '南充市', '眉山市', '宜宾市', '广安市', '达州市', '雅安市', '巴中市', '资阳市', '阿坝藏族羌族自治州', '甘孜藏族自治州', '凉山彝族自治州'] },
  { name: '贵州省', cities: ['贵阳市', '六盘水市', '遵义市', '安顺市', '毕节市', '铜仁市', '黔西南布依族苗族自治州', '黔东南苗族侗族自治州', '黔南布依族苗族自治州'] },
  { name: '云南省', cities: ['昆明市', '曲靖市', '玉溪市', '保山市', '昭通市', '丽江市', '普洱市', '临沧市', '楚雄彝族自治州', '红河哈尼族彝族自治州', '文山壮族苗族自治州', '西双版纳傣族自治州', '大理白族自治州', '德宏傣族景颇族自治州', '怒江傈僳族自治州', '迪庆藏族自治州'] },
  { name: '陕西省', cities: ['西安市', '铜川市', '宝鸡市', '咸阳市', '渭南市', '延安市', '汉中市', '榆林市', '安康市', '商洛市'] },
  { name: '甘肃省', cities: ['兰州市', '嘉峪关市', '金昌市', '白银市', '天水市', '武威市', '张掖市', '平凉市', '酒泉市', '庆阳市', '定西市', '陇南市', '临夏回族自治州', '甘南藏族自治州'] },
  { name: '青海省', cities: ['西宁市', '海东市', '海北藏族自治州', '黄南藏族自治州', '海南藏族自治州', '果洛藏族自治州', '玉树藏族自治州', '海西蒙古族藏族自治州'] },
  { name: '台湾省', cities: ['台北市', '新北市', '桃园市', '台中市', '台南市', '高雄市'] },
  { name: '内蒙古自治区', cities: ['呼和浩特市', '包头市', '乌海市', '赤峰市', '通辽市', '鄂尔多斯市', '呼伦贝尔市', '巴彦淖尔市', '乌兰察布市', '兴安盟', '锡林郭勒盟', '阿拉善盟'] },
  { name: '广西壮族自治区', cities: ['南宁市', '柳州市', '桂林市', '梧州市', '北海市', '防城港市', '钦州市', '贵港市', '玉林市', '百色市', '贺州市', '河池市', '来宾市', '崇左市'] },
  { name: '西藏自治区', cities: ['拉萨市', '日喀则市', '昌都市', '林芝市', '山南市', '那曲市', '阿里地区'] },
  { name: '宁夏回族自治区', cities: ['银川市', '石嘴山市', '吴忠市', '固原市', '中卫市'] },
  { name: '新疆维吾尔自治区', cities: ['乌鲁木齐市', '克拉玛依市', '吐鲁番市', '哈密市', '昌吉回族自治州', '博尔塔拉蒙古自治州', '巴音郭楞蒙古自治州', '阿克苏地区', '克孜勒苏柯尔克孜自治州', '喀什地区', '和田地区', '伊犁哈萨克自治州', '塔城地区', '阿勒泰地区'] },
];

const DISTRICTS: Record<string, string[]> = {
  '北京市': ['东城区', '西城区', '朝阳区', '丰台区', '石景山区', '海淀区', '门头沟区', '房山区', '通州区', '顺义区', '昌平区', '大兴区', '怀柔区', '平谷区', '密云区', '延庆区'],
  '天津市': ['和平区', '河东区', '河西区', '南开区', '河北区', '红桥区', '东丽区', '西青区', '津南区', '北辰区', '武清区', '宝坻区', '滨海新区', '宁河区', '静海区', '蓟州区'],
  '上海市': ['黄浦区', '徐汇区', '长宁区', '静安区', '普陀区', '虹口区', '杨浦区', '闵行区', '宝山区', '嘉定区', '浦东新区', '金山区', '松江区', '青浦区', '奉贤区', '崇明区'],
  '重庆市': ['渝中区', '大渡口区', '江北区', '沙坪坝区', '九龙坡区', '南岸区', '北碚区', '渝北区', '巴南区', '涪陵区', '万州区', '黔江区', '长寿区', '江津区', '合川区', '永川区', '南川区', '綦江区', '大足区', '璧山区', '铜梁区', '潼南区', '荣昌区', '开州区', '梁平区', '武隆区'],
  // 河北省
  '石家庄市': ['长安区', '桥西区', '新华区', '井陉矿区', '裕华区', '藁城区', '鹿泉区', '栾城区', '井陉县', '正定县', '行唐县', '灵寿县', '高邑县', '深泽县', '赞皇县', '无极县', '平山县', '元氏县', '赵县', '辛集市', '晋州市', '新乐市'],
  '唐山市': ['路南区', '路北区', '古冶区', '开平区', '丰南区', '丰润区', '曹妃甸区', '滦南县', '乐亭县', '迁西县', '玉田县', '遵化市', '迁安市', '滦州市'],
  '秦皇岛市': ['海港区', '山海关区', '北戴河区', '抚宁区', '青龙满族自治县', '昌黎县', '卢龙县'],
  '邯郸市': ['邯山区', '丛台区', '复兴区', '峰峰矿区', '肥乡区', '永年区', '临漳县', '成安县', '大名县', '涉县', '磁县', '邱县', '鸡泽县', '广平县', '馆陶县', '魏县', '曲周县', '武安市'],
  '邢台市': ['襄都区', '信都区', '任泽区', '南和区', '临城县', '内丘县', '柏乡县', '隆尧县', '宁晋县', '巨鹿县', '新河县', '广宗县', '平乡县', '威县', '清河县', '临西县', '南宫市', '沙河市'],
  '保定市': ['竞秀区', '莲池区', '满城区', '清苑区', '徐水区', '涞水县', '阜平县', '定兴县', '唐县', '高阳县', '容城县', '涞源县', '望都县', '安新县', '易县', '曲阳县', '蠡县', '顺平县', '博野县', '雄县', '涿州市', '定州市', '安国市', '高碑店市'],
  '张家口市': ['桥东区', '桥西区', '宣化区', '下花园区', '万全区', '崇礼区', '张北县', '康保县', '沽源县', '尚义县', '蔚县', '阳原县', '怀安县', '怀来县', '涿鹿县', '赤城县'],
  '承德市': ['双桥区', '双滦区', '鹰手营子矿区', '承德县', '兴隆县', '滦平县', '隆化县', '丰宁满族自治县', '宽城满族自治县', '围场满族蒙古族自治县', '平泉市'],
  '沧州市': ['新华区', '运河区', '沧县', '青县', '东光县', '海兴县', '盐山县', '肃宁县', '南皮县', '吴桥县', '献县', '孟村回族自治县', '泊头市', '任丘市', '黄骅市', '河间市'],
  '廊坊市': ['安次区', '广阳区', '固安县', '永清县', '香河县', '大城县', '文安县', '大厂回族自治县', '霸州市', '三河市'],
  '衡水市': ['桃城区', '冀州区', '枣强县', '武邑县', '武强县', '饶阳县', '安平县', '故城县', '景县', '阜城县', '深州市'],
  // 山西省
  '太原市': ['小店区', '迎泽区', '杏花岭区', '尖草坪区', '万柏林区', '晋源区', '清徐县', '阳曲县', '娄烦县', '古交市'],
  '大同市': ['新荣区', '平城区', '云冈区', '云州区', '阳高县', '天镇县', '广灵县', '灵丘县', '浑源县', '左云县'],
  '阳泉市': ['城区', '矿区', '郊区', '平定县', '盂县'],
  '长治市': ['潞州区', '上党区', '屯留区', '潞城区', '襄垣县', '平顺县', '黎城县', '壶关县', '长子县', '武乡县', '沁县', '沁源县'],
  '晋城市': ['城区', '沁水县', '阳城县', '陵川县', '泽州县', '高平市'],
  '朔州市': ['朔城区', '平鲁区', '山阴县', '应县', '右玉县', '怀仁市'],
  '晋中市': ['榆次区', '太谷区', '榆社县', '左权县', '和顺县', '昔阳县', '寿阳县', '祁县', '平遥县', '灵石县', '介休市'],
  '运城市': ['盐湖区', '临猗县', '万荣县', '闻喜县', '稷山县', '新绛县', '绛县', '垣曲县', '夏县', '平陆县', '芮城县', '永济市', '河津市'],
  '忻州市': ['忻府区', '定襄县', '五台县', '代县', '繁峙县', '宁武县', '静乐县', '神池县', '五寨县', '岢岚县', '河曲县', '保德县', '偏关县', '原平市'],
  '临汾市': ['尧都区', '曲沃县', '翼城县', '襄汾县', '洪洞县', '古县', '安泽县', '浮山县', '吉县', '乡宁县', '大宁县', '隰县', '永和县', '蒲县', '汾西县', '侯马市', '霍州市'],
  '吕梁市': ['离石区', '文水县', '交城县', '兴县', '临县', '柳林县', '石楼县', '岚县', '方山县', '中阳县', '交口县', '孝义市', '汾阳市'],
  // 辽宁省
  '沈阳市': ['和平区', '沈河区', '大东区', '皇姑区', '铁西区', '苏家屯区', '浑南区', '沈北新区', '于洪区', '辽中区', '康平县', '法库县', '新民市'],
  '大连市': ['中山区', '西岗区', '沙河口区', '甘井子区', '旅顺口区', '金州区', '普兰店区', '长海县', '瓦房店市', '庄河市'],
  '鞍山市': ['铁东区', '铁西区', '立山区', '千山区', '台安县', '岫岩满族自治县', '海城市'],
  '抚顺市': ['新抚区', '东洲区', '望花区', '顺城区', '抚顺县', '新宾满族自治县', '清原满族自治县'],
  '本溪市': ['平山区', '溪湖区', '明山区', '南芬区', '本溪满族自治县', '桓仁满族自治县'],
  '丹东市': ['元宝区', '振兴区', '振安区', '宽甸满族自治县', '东港市', '凤城市'],
  '锦州市': ['古塔区', '凌河区', '太和区', '黑山县', '义县', '凌海市', '北镇市'],
  '营口市': ['站前区', '西市区', '鲅鱼圈区', '老边区', '盖州市', '大石桥市'],
  '阜新市': ['海州区', '新邱区', '太平区', '清河门区', '细河区', '阜新蒙古族自治县', '彰武县'],
  '辽阳市': ['白塔区', '文圣区', '宏伟区', '弓长岭区', '太子河区', '辽阳县', '灯塔市'],
  '盘锦市': ['双台子区', '兴隆台区', '大洼区', '盘山县'],
  '铁岭市': ['银州区', '清河区', '铁岭县', '西丰县', '昌图县', '调兵山市', '开原市'],
  '朝阳市': ['双塔区', '龙城区', '朝阳县', '建平县', '喀喇沁左翼蒙古族自治县', '北票市', '凌源市'],
  '葫芦岛市': ['连山区', '龙港区', '南票区', '绥中县', '建昌县', '兴城市'],
  // 吉林省
  '长春市': ['南关区', '宽城区', '朝阳区', '二道区', '绿园区', '双阳区', '九台区', '农安县', '榆树市', '德惠市', '公主岭市'],
  '吉林市': ['昌邑区', '龙潭区', '船营区', '丰满区', '永吉县', '蛟河市', '桦甸市', '舒兰市', '磐石市'],
  '四平市': ['铁西区', '铁东区', '梨树县', '伊通满族自治县', '双辽市'],
  '辽源市': ['龙山区', '西安区', '东丰县', '东辽县'],
  '通化市': ['东昌区', '二道江区', '通化县', '辉南县', '柳河县', '梅河口市', '集安市'],
  '白山市': ['浑江区', '江源区', '抚松县', '靖宇县', '长白朝鲜族自治县', '临江市'],
  '松原市': ['宁江区', '前郭尔罗斯蒙古族自治县', '长岭县', '乾安县', '扶余市'],
  '白城市': ['洮北区', '镇赉县', '通榆县', '洮南市', '大安市'],
  '延边朝鲜族自治州': ['延吉市', '图们市', '敦化市', '珲春市', '龙井市', '和龙市', '汪清县', '安图县'],
  // 黑龙江省
  '哈尔滨市': ['道里区', '南岗区', '道外区', '平房区', '松北区', '香坊区', '呼兰区', '阿城区', '双城区', '依兰县', '方正县', '宾县', '巴彦县', '木兰县', '通河县', '延寿县', '尚志市', '五常市'],
  '齐齐哈尔市': ['龙沙区', '建华区', '铁锋区', '昂昂溪区', '富拉尔基区', '碾子山区', '梅里斯达斡尔族区', '龙江县', '依安县', '泰来县', '甘南县', '富裕县', '克山县', '克东县', '拜泉县', '讷河市'],
  '鸡西市': ['鸡冠区', '恒山区', '滴道区', '梨树区', '城子河区', '麻山区', '鸡东县', '虎林市', '密山市'],
  '鹤岗市': ['向阳区', '工农区', '南山区', '兴安区', '东山区', '兴山区', '萝北县', '绥滨县'],
  '双鸭山市': ['尖山区', '岭东区', '四方台区', '宝山区', '集贤县', '友谊县', '宝清县', '饶河县'],
  '大庆市': ['萨尔图区', '龙凤区', '让胡路区', '红岗区', '大同区', '肇州县', '肇源县', '林甸县', '杜尔伯特蒙古族自治县'],
  '伊春市': ['伊美区', '乌翠区', '友好区', '嘉荫县', '汤旺县', '丰林县', '大箐山县', '南岔县', '金林区', '铁力市'],
  '佳木斯市': ['向阳区', '前进区', '东风区', '郊区', '桦南县', '桦川县', '汤原县', '同江市', '富锦市', '抚远市'],
  '七台河市': ['新兴区', '桃山区', '茄子河区', '勃利县'],
  '牡丹江市': ['东安区', '阳明区', '爱民区', '西安区', '林口县', '绥芬河市', '海林市', '宁安市', '穆棱市', '东宁市'],
  '黑河市': ['爱辉区', '逊克县', '孙吴县', '北安市', '五大连池市', '嫩江市'],
  '绥化市': ['北林区', '望奎县', '兰西县', '青冈县', '庆安县', '明水县', '绥棱县', '安达市', '肇东市', '海伦市'],
  '大兴安岭地区': ['漠河市', '呼玛县', '塔河县'],
  // 江苏省
  '南京市': ['玄武区', '秦淮区', '建邺区', '鼓楼区', '浦口区', '栖霞区', '雨花台区', '江宁区', '六合区', '溧水区', '高淳区'],
  '无锡市': ['梁溪区', '锡山区', '惠山区', '滨湖区', '新吴区', '江阴市', '宜兴市'],
  '徐州市': ['鼓楼区', '云龙区', '贾汪区', '泉山区', '铜山区', '丰县', '沛县', '睢宁县', '新沂市', '邳州市'],
  '常州市': ['天宁区', '钟楼区', '新北区', '武进区', '金坛区', '溧阳市'],
  '苏州市': ['姑苏区', '虎丘区', '吴中区', '相城区', '吴江区', '常熟市', '张家港市', '昆山市', '太仓市'],
  '南通市': ['崇川区', '通州区', '海门区', '如东县', '启东市', '如皋市', '海安市'],
  '连云港市': ['连云区', '海州区', '赣榆区', '东海县', '灌云县', '灌南县'],
  '淮安市': ['淮安区', '淮阴区', '清江浦区', '洪泽区', '涟水县', '盱眙县', '金湖县'],
  '盐城市': ['亭湖区', '盐都区', '大丰区', '响水县', '滨海县', '阜宁县', '射阳县', '建湖县', '东台市'],
  '扬州市': ['广陵区', '邗江区', '江都区', '宝应县', '仪征市', '高邮市'],
  '镇江市': ['京口区', '润州区', '丹徒区', '丹阳市', '扬中市', '句容市'],
  '泰州市': ['海陵区', '高港区', '姜堰区', '兴化市', '靖江市', '泰兴市'],
  '宿迁市': ['宿城区', '宿豫区', '沭阳县', '泗阳县', '泗洪县'],
  // 浙江省
  '杭州市': ['上城区', '拱墅区', '西湖区', '滨江区', '萧山区', '余杭区', '富阳区', '临安区', '临平区', '钱塘区', '桐庐县', '淳安县', '建德市'],
  '宁波市': ['海曙区', '江北区', '北仑区', '镇海区', '鄞州区', '奉化区', '象山县', '宁海县', '余姚市', '慈溪市'],
  '温州市': ['鹿城区', '龙湾区', '瓯海区', '洞头区', '永嘉县', '平阳县', '苍南县', '文成县', '泰顺县', '瑞安市', '乐清市', '龙港市'],
  '嘉兴市': ['南湖区', '秀洲区', '嘉善县', '海盐县', '海宁市', '平湖市', '桐乡市'],
  '湖州市': ['吴兴区', '南浔区', '德清县', '长兴县', '安吉县'],
  '绍兴市': ['越城区', '柯桥区', '上虞区', '新昌县', '诸暨市', '嵊州市'],
  '金华市': ['婺城区', '金东区', '武义县', '浦江县', '磐安县', '兰溪市', '义乌市', '东阳市', '永康市'],
  '衢州市': ['柯城区', '衢江区', '常山县', '开化县', '龙游县', '江山市'],
  '舟山市': ['定海区', '普陀区', '岱山县', '嵊泗县'],
  '台州市': ['椒江区', '黄岩区', '路桥区', '三门县', '天台县', '仙居县', '温岭市', '临海市', '玉环市'],
  '丽水市': ['莲都区', '青田县', '缙云县', '遂昌县', '松阳县', '云和县', '庆元县', '景宁畲族自治县', '龙泉市'],
  // 安徽省
  '合肥市': ['瑶海区', '庐阳区', '蜀山区', '包河区', '长丰县', '肥东县', '肥西县', '庐江县', '巢湖市'],
  '芜湖市': ['镜湖区', '鸠江区', '弋江区', '湾沚区', '繁昌区', '南陵县', '无为市'],
  '蚌埠市': ['龙子湖区', '蚌山区', '禹会区', '淮上区', '怀远县', '五河县', '固镇县'],
  '淮南市': ['大通区', '田家庵区', '谢家集区', '八公山区', '潘集区', '凤台县', '寿县'],
  '马鞍山市': ['花山区', '雨山区', '博望区', '当涂县', '含山县', '和县'],
  '淮北市': ['杜集区', '相山区', '烈山区', '濉溪县'],
  '铜陵市': ['铜官区', '义安区', '郊区', '枞阳县'],
  '安庆市': ['迎江区', '大观区', '宜秀区', '怀宁县', '太湖县', '宿松县', '望江县', '岳西县', '桐城市', '潜山市'],
  '黄山市': ['屯溪区', '黄山区', '徽州区', '歙县', '休宁县', '黟县', '祁门县'],
  '滁州市': ['琅琊区', '南谯区', '来安县', '全椒县', '定远县', '凤阳县', '天长市', '明光市'],
  '阜阳市': ['颍州区', '颍东区', '颍泉区', '临泉县', '太和县', '阜南县', '颍上县', '界首市'],
  '宿州市': ['埇桥区', '砀山县', '萧县', '灵璧县', '泗县'],
  '六安市': ['金安区', '裕安区', '叶集区', '霍邱县', '舒城县', '金寨县', '霍山县'],
  '亳州市': ['谯城区', '涡阳县', '蒙城县', '利辛县'],
  '池州市': ['贵池区', '东至县', '石台县', '青阳县'],
  '宣城市': ['宣州区', '郎溪县', '泾县', '绩溪县', '旌德县', '宁国市', '广德市'],
  // 福建省
  '福州市': ['鼓楼区', '台江区', '仓山区', '马尾区', '晋安区', '长乐区', '闽侯县', '连江县', '罗源县', '闽清县', '永泰县', '平潭县', '福清市'],
  '厦门市': ['思明区', '海沧区', '湖里区', '集美区', '同安区', '翔安区'],
  '莆田市': ['城厢区', '涵江区', '荔城区', '秀屿区', '仙游县'],
  '三明市': ['三元区', '沙县区', '明溪县', '清流县', '宁化县', '大田县', '尤溪县', '将乐县', '泰宁县', '新干县', '永安市'],
  '泉州市': ['鲤城区', '丰泽区', '洛江区', '泉港区', '惠安县', '安溪县', '永春县', '德化县', '金门县', '石狮市', '晋江市', '南安市'],
  '漳州市': ['芗城区', '龙文区', '龙海区', '长泰区', '云霄县', '漳浦县', '诏安县', '东山县', '南靖县', '平和县', '华安县'],
  '南平市': ['延平区', '建阳区', '顺昌县', '浦城县', '光泽县', '松溪县', '政和县', '邵武市', '武夷山市', '建瓯市'],
  '龙岩市': ['新罗区', '永定区', '长汀县', '上杭县', '武平县', '连城县', '漳平市'],
  '宁德市': ['蕉城区', '霞浦县', '古田县', '屏南县', '寿宁县', '周宁县', '柘荣县', '福安市', '福鼎市'],
  // 江西省
  '南昌市': ['东湖区', '西湖区', '青云谱区', '青山湖区', '新建区', '红谷滩区', '南昌县', '安义县', '进贤县'],
  '景德镇市': ['昌江区', '珠山区', '浮梁县', '乐平市'],
  '萍乡市': ['安源区', '湘东区', '莲花县', '上栗县', '芦溪县'],
  '九江市': ['濂溪区', '浔阳区', '柴桑区', '武宁县', '修水县', '永修县', '德安县', '都昌县', '湖口县', '彭泽县', '瑞昌市', '共青城市', '庐山市'],
  '新余市': ['渝水区', '分宜县'],
  '鹰潭市': ['月湖区', '余江区', '贵溪市'],
  '赣州市': ['章贡区', '南康区', '赣县区', '信丰县', '大余县', '上犹县', '崇义县', '安远县', '龙南县', '宁都县', '于都县', '兴国县', '会昌县', '寻乌县', '石城县', '瑞金市', '龙南市'],
  '吉安市': ['吉州区', '青原区', '吉安县', '吉水县', '峡江县', '新干县', '永丰县', '泰和县', '遂川县', '万安县', '安福县', '永新县', '井冈山市'],
  '宜春市': ['袁州区', '奉新县', '万载县', '上高县', '宜丰县', '靖安县', '铜鼓县', '丰城市', '樟树市', '高安市'],
  '抚州市': ['临川区', '东乡区', '南城县', '黎川县', '南丰县', '崇仁县', '乐安县', '宜黄县', '金溪县', '资溪县', '广昌县'],
  '上饶市': ['信州区', '广丰区', '广信区', '玉山县', '铅山县', '横峰县', '弋阳县', '余干县', '鄱阳县', '万年县', '婺源县', '德兴市'],
  // 山东省
  '济南市': ['历下区', '市中区', '槐荫区', '天桥区', '历城区', '长清区', '章丘区', '济阳区', '莱芜区', '钢城区', '平阴县', '商河县'],
  '青岛市': ['市南区', '市北区', '黄岛区', '崂山区', '李沧区', '城阳区', '即墨区', '胶州市', '平度市', '莱西市'],
  '淄博市': ['淄川区', '张店区', '博山区', '临淄区', '周村区', '桓台县', '高青县', '沂源县'],
  '枣庄市': ['市中区', '薛城区', '峄城区', '台儿庄区', '山亭区', '滕州市'],
  '东营市': ['东营区', '河口区', '垦利区', '利津县', '广饶县'],
  '烟台市': ['芝罘区', '福山区', '牟平区', '莱山区', '蓬莱区', '龙口市', '莱阳市', '莱州市', '招远市', '栖霞市', '海阳市'],
  '潍坊市': ['潍城区', '寒亭区', '坊子区', '奎文区', '临朐县', '昌乐县', '青州市', '诸城市', '寿光市', '安丘市', '高密市', '昌邑市'],
  '济宁市': ['任城区', '兖州区', '微山县', '鱼台县', '金乡县', '嘉祥县', '汶上县', '泗水县', '梁山县', '曲阜市', '邹城市'],
  '泰安市': ['泰山区', '岱岳区', '宁阳县', '东平县', '新泰市', '肥城市'],
  '威海市': ['环翠区', '文登区', '荣成市', '乳山市'],
  '日照市': ['东港区', '岚山区', '五莲县', '莒县'],
  '临沂市': ['兰山区', '罗庄区', '河东区', '沂南县', '郯城县', '沂水县', '兰陵县', '费县', '平邑县', '莒南县', '蒙阴县', '临沭县'],
  '德州市': ['德城区', '陵城区', '宁津县', '庆云县', '临邑县', '齐河县', '平原县', '夏津县', '武城县', '乐陵市', '禹城市'],
  '聊城市': ['东昌府区', '茌平区', '阳谷县', '莘县', '东阿县', '冠县', '高唐县', '临清市'],
  '滨州市': ['滨城区', '沾化区', '惠民县', '阳信县', '无棣县', '博兴县', '邹平市'],
  '菏泽市': ['牡丹区', '定陶区', '曹县', '单县', '成武县', '巨野县', '郓城县', '鄄城县', '东明县'],
  // 河南省
  '郑州市': ['中原区', '二七区', '管城回族区', '金水区', '上街区', '惠济区', '中牟县', '巩义市', '荥阳市', '新密市', '新郑市', '登封市'],
  '开封市': ['龙亭区', '顺河回族区', '鼓楼区', '禹王台区', '祥符区', '杞县', '通许县', '尉氏县', '兰考县'],
  '洛阳市': ['老城区', '西工区', '瀍河回族区', '涧西区', '偃师区', '孟津区', '洛龙区', '新安县', '栾川县', '嵩县', '汝阳县', '宜阳县', '洛宁县', '伊川县'],
  '平顶山市': ['新华区', '卫滨区', '石龙区', '湛河区', '宝丰县', '叶县', '鲁山县', '郏县', '舞钢市', '汝州市'],
  '安阳市': ['文峰区', '北关区', '殷都区', '龙安区', '安阳县', '汤阴县', '滑县', '内黄县', '林州市'],
  '鹤壁市': ['鹤山区', '山城区', '淇滨区', '浚县', '淇县'],
  '新乡市': ['红旗区', '卫滨区', '凤泉区', '牧野区', '新乡县', '获嘉县', '原阳县', '延津县', '封丘县', '卫辉市', '辉县市', '长垣市'],
  '焦作市': ['解放区', '中站区', '马村区', '山阳区', '修武县', '博爱县', '武陟县', '温县', '沁阳市', '孟州市'],
  '濮阳市': ['华龙区', '清丰县', '南乐县', '范县', '台前县', '濮阳县'],
  '许昌市': ['魏都区', '建安区', '鄢陵县', '襄城县', '禹州市', '长葛市'],
  '漯河市': ['源汇区', '郾城区', '召陵区', '舞阳县', '临颍县'],
  '三门峡市': ['湖滨区', '陕州区', '渑池县', '卢氏县', '义马市', '灵宝市'],
  '南阳市': ['宛城区', '卧龙区', '南召县', '方城县', '西峡县', '镇平县', '内乡县', '淅川县', '社旗县', '唐河县', '新野县', '桐柏县', '邓州市'],
  '商丘市': ['梁园区', '睢阳区', '民权县', '睢县', '宁陵县', '柘城县', '虞城县', '夏邑县', '永城市'],
  '信阳市': ['浉河区', '平桥区', '罗山县', '光山县', '新县', '商城县', '固始县', '潢川县', '淮滨县', '息县'],
  '周口市': ['川汇区', '淮阳区', '扶沟县', '西华县', '商水县', '沈丘县', '郸城县', '太康县', '鹿邑县', '项城市'],
  '驻马店市': ['驿城区', '西平县', '上蔡县', '平舆县', '正阳县', '确山县', '泌阳县', '汝南县', '遂平县', '新蔡县', '济源市'],
  // 湖北省
  '武汉市': ['江岸区', '江汉区', '硚口区', '汉阳区', '武昌区', '青山区', '洪山区', '东西湖区', '汉南区', '蔡甸区', '江夏区', '黄陂区', '新洲区'],
  '黄石市': ['黄石港区', '西塞山区', '下陆区', '铁山区', '阳新县', '大冶市'],
  '十堰市': ['茅箭区', '张湾区', '郧阳区', '郧西县', '竹山县', '竹溪县', '房县', '丹江口市'],
  '宜昌市': ['西陵区', '伍家岗区', '点军区', '猇亭区', '夷陵区', '远安县', '兴山县', '秭归县', '长阳土家族自治县', '五峰土家族自治县', '宜都市', '当阳市', '枝江市'],
  '襄阳市': ['襄城区', '樊城区', '襄州区', '南漳县', '谷城县', '保康县', '老河口市', '枣阳市', '宜城市'],
  '鄂州市': ['梁子湖区', '华容区', '鄂城区'],
  '荆门市': ['东宝区', '掇刀区', '沙洋县', '钟祥市', '京山市'],
  '孝感市': ['孝南区', '孝昌区', '大悟区', '云梦区', '应城市', '安陆市'],
  '荆州市': ['沙市区', '荆州区', '公安县', '监利市', '江陵县', '石首市', '洪湖市', '松滋市'],
  '黄冈市': ['黄州区', '团风县', '红安县', '罗田县', '英山县', '浠水县', '蕲春县', '黄梅县', '麻城市', '武穴市'],
  '咸宁市': ['咸安区', '嘉鱼县', '通城县', '赤壁市', '崇阳县', '通山县'],
  '随州市': ['曾都区', '随县', '广水市'],
  '恩施土家族苗族自治州': ['恩施市', '利川市', '建始县', '巴东县', '宣恩县', '咸丰县', '来凤县', '鹤峰县'],
  // 湖南省
  '长沙市': ['芙蓉区', '天心区', '岳麓区', '开福区', '雨花区', '望城区', '长沙县', '浏阳市', '宁乡市'],
  '株洲市': ['荷塘区', '芦淞区', '石峰区', '天元区', '渌口区', '攸县', '茶陵县', '炎陵县', '醴陵市'],
  '湘潭市': ['雨湖区', '岳塘区', '湘潭县', '湘乡市', '韶山市'],
  '衡阳市': ['珠晖区', '雁峰区', '石鼓区', '蒸湘区', '南岳区', '衡阳县', '衡南县', '衡山县', '衡东县', '祁东县', '耒阳市', '常宁市'],
  '邵阳市': ['双清区', '大祥区', '北塔区', '新邵县', '邵阳县', '隆回县', '洞口县', '绥宁县', '新宁县', '城步苗族自治县', '武冈市', '邵东市'],
  '岳阳市': ['岳阳楼区', '云溪区', '君山区', '岳阳县', '华容县', '湘阴县', '平江县', '汨罗市', '临湘市'],
  '常德市': ['武陵区', '鼎城区', '安乡县', '汉寿县', '澧县', '临澧县', '桃源县', '石门县', '津市市'],
  '张家界市': ['永定区', '武陵源区', '慈利县', '桑植县'],
  '益阳市': ['资阳区', '赫山区', '南县', '桃江县', '安化县', '沅江市'],
  '郴州市': ['北湖区', '苏仙区', '桂阳县', '宜章县', '永兴县', '嘉禾县', '临武县', '汝城县', '桂东县', '安仁县', '资兴市'],
  '永州市': ['零陵区', '冷水滩区', '东安县', '双牌县', '道县', '江永县', '宁远县', '蓝山县', '新田县', '江华瑶族自治县', '祁阳市'],
  '怀化市': ['鹤城区', '中方县', '沅陵县', '辰溪县', '溆浦县', '会同县', '麻阳苗族自治县', '新晃侗族自治县', '芷江侗族自治县', '靖州苗族侗族自治县', '通道侗族自治县', '洪江市'],
  '娄底市': ['娄星区', '双峰县', '新化县', '冷水江市', '涟源市'],
  '湘西土家族苗族自治州': ['吉首市', '泸溪县', '凤凰县', '花垣县', '保靖县', '古丈县', '永顺县', '龙山县'],
  // 广东省
  '广州市': ['兴宁区', '青秀区', '江南区', '西乡塘区', '良庆区', '邕宁区', '武鸣区', '隆安县', '马山县', '上林县', '宾阳县', '横州市'],
  '韶关市': ['武江区', '浈江区', '曲江区', '始兴县', '仁化县', '翁源县', '乳源瑶族自治县', '新丰县', '乐昌市', '南雄市'],
  '深圳市': ['罗湖区', '福田区', '南山区', '宝安区', '龙岗区', '盐田区', '龙华区', '坪山区', '光明区'],
  '珠海市': ['香洲区', '斗门区', '金湾区'],
  '汕头市': ['龙湖区', '金平区', '濠江区', '潮阳区', '潮南区', '澄海区', '南澳县'],
  '佛山市': ['禅城区', '南海区', '顺德区', '三水区', '高明区'],
  '江门市': ['蓬江区', '江海区', '新会区', '台山市', '开平市', '鹤山市', '恩平市'],
  '湛江市': ['赤坎区', '霞山区', '坡头区', '麻章区', '遂溪县', '徐闻县', '廉江市', '雷州市', '吴川市'],
  '茂名市': ['茂南区', '电白区', '高州市', '化州市', '信宜市'],
  '肇庆市': ['端州区', '鼎湖区', '高要区', '广宁县', '怀集县', '封开县', '德庆县', '四会市'],
  '惠州市': ['惠城区', '惠阳区', '博罗县', '惠东县', '龙门县'],
  '梅州市': ['梅江区', '梅县区', '大埔县', '丰顺县', '五华县', '平远县', '蕉岭县', '兴宁市'],
  '汕尾市': ['城区', '海丰县', '陆河县', '陆丰市'],
  '河源市': ['源城区', '紫金县', '龙川县', '连平县', '和平县', '东源县'],
  '阳江市': ['江城区', '阳东区', '阳西县', '阳春市'],
  '清远市': ['清城区', '清新区', '佛冈县', '阳山县', '连山壮族瑶族自治县', '连南瑶族自治县', '英德市', '连州市'],
  '东莞市': ['东城街道', '南城街道', '万江街道', '莞城街道', '石碣镇', '石龙镇', '茶山镇', '石排镇', '企石镇', '横沥镇', '桥头镇', '谢岗镇', '东坑镇', '常平镇', '寮步镇', '樟木头镇', '大朗镇', '黄江镇', '清溪镇', '塘厦镇', '凤岗镇', '大岭山镇', '长安镇', '虎门镇', '厚街镇', '沙田镇', '道滘镇', '洪梅镇', '麻涌镇', '望牛墩镇', '中堂镇', '高埗镇', '松山湖', '石排镇'],
  '中山市': ['石岐街道', '东区街道', '西区街道', '南区街道', '五桂山街道', '中山港街道', '黄圃镇', '南头镇', '东凤镇', '阜沙镇', '小榄镇', '东升镇', '古镇镇', '横栏镇', '三角镇', '民众镇', '南朗镇', '港口镇', '大涌镇', '沙溪镇', '三乡镇', '板芙镇', '神湾镇', '坦洲镇'],
  '潮州市': ['湘桥区', '潮安区', '饶平县'],
  '揭阳市': ['榕城区', '揭东区', '揭西县', '惠来县', '普宁市'],
  '云浮市': ['云城区', '云安区', '新兴县', '郁南县', '罗定市'],
  // 海南省
  '海口市': ['秀英区', '龙华区', '琼山区', '美兰区'],
  '三亚市': ['海棠区', '吉阳区', '天涯区', '崖州区'],
  '三沙市': ['西沙群岛', '南沙群岛', '中沙群岛'],
  '儋州市': ['那大镇', '和庆镇', '南丰镇', '大成镇', '雅星镇', '兰洋镇', '光村镇', '木棠镇', '海头镇', '峨蔓镇', '王五镇', '白马井镇', '中和镇', '排浦镇', '东成镇', '新州镇'],
  // 四川省
  '成都市': ['锦江区', '青羊区', '金牛区', '武侯区', '成华区', '龙泉驿区', '青白江区', '新都区', '温江区', '双流区', '郫都区', '新津区', '金堂县', '大邑县', '蒲江县', '都江堰市', '彭州市', '邛崃市', '崇州市', '简阳市'],
  '自贡市': ['自流井区', '贡井区', '大安区', '沿滩区', '荣县', '富顺县'],
  '攀枝花市': ['东区', '西区', '仁和区', '米易县', '盐边县'],
  '泸州市': ['江阳区', '纳溪区', '龙马潭区', '泸县', '合江县', '叙永县', '古蔺县'],
  '德阳市': ['旌阳区', '罗江区', '中江县', '广汉市', '什邡市', '绵竹市'],
  '绵阳市': ['涪城区', '游仙区', '安州区', '三台县', '盐亭县', '梓潼县', '北川县', '平武县', '江油市'],
  '广元市': ['利州区', '昭化区', '朝天区', '旺苍县', '青川县', '剑阁县', '苍溪县'],
  '遂宁市': ['船山区', '安居区', '蓬溪县', '大英县', '射洪市'],
  '内江市': ['市中区', '东兴区', '威远县', '资中县', '隆昌市'],
  '乐山市': ['市中区', '沙湾区', '五通桥区', '金口河区', '犍为县', '井研县', '夹江县', '沐川县', '峨边彝族自治县', '马边彝族自治县', '峨眉山市'],
  '南充市': ['顺庆区', '高坪区', '嘉陵区', '南部县', '营山县', '蓬安县', '仪陇县', '西充县', '阆中市'],
  '眉山市': ['东坡区', '彭山区', '仁寿县', '洪雅县', '丹棱县', '青神县'],
  '宜宾市': ['翠屏区', '南溪区', '叙州区', '江安县', '长宁县', '高县', '珙县', '筠连县', '兴文县', '屏山县'],
  '广安市': ['广安区', '前锋区', '岳池县', '武胜县', '邻水县', '华蓥市'],
  '达州市': ['通川区', '达川区', '宣汉县', '开江县', '大竹县', '渠县', '万源市'],
  '雅安市': ['雨城区', '名山区', '荥经县', '汉源县', '石棉县', '天全县', '芦山县', '宝兴县'],
  '巴中市': ['巴州区', '恩阳区', '通江县', '南江县', '平昌县'],
  '资阳市': ['雁江区', '安岳县', '乐至县'],
  '阿坝藏族羌族自治州': ['马尔康市', '汶川县', '理县', '茂县', '松潘县', '九寨沟县', '金川县', '小金县', '黑水县', '壤塘县', '阿坝县', '若尔盖县', '红原县'],
  '甘孜藏族自治州': ['康定市', '泸定县', '丹巴县', '九龙县', '雅江县', '道孚县', '炉霍县', '甘孜县', '新龙县', '德格县', '白玉县', '石渠县', '色达县', '理塘县', '巴塘县', '乡城县', '稻城县', '得荣县'],
  '凉山彝族自治州': ['西昌市', '会理市', '木里藏族自治县', '盐源县', '德昌县', '会东县', '宁南县', '普格县', '冕宁县', '越西县', '甘洛县', '美姑县', '雷波县'],
  // 贵州省
  '贵阳市': ['南明区', '云岩区', '花溪区', '乌当区', '白云区', '观山湖区', '开阳县', '息烽县', '修文县', '清镇市'],
  '六盘水市': ['钟山区', '六枝特区', '水城区', '盘州市'],
  '遵义市': ['红花岗区', '汇川区', '播州区', '桐梓县', '绥阳县', '正安县', '道真仡佬族苗族自治县', '务川仡佬族苗族自治县', '凤冈县', '湄潭县', '余庆县', '习水县', '赤水市', '仁怀市'],
  '安顺市': ['西秀区', '平坝区', '普定县', '镇宁布依族苗族自治县', '关岭布依族苗族自治县', '紫云苗族布依族自治县'],
  '毕节市': ['七星关区', '大方县', '金沙县', '织金县', '纳雍县', '威宁彝族回族苗族自治县', '赫章县', '黔西市'],
  '铜仁市': ['碧江区', '万山区', '江口县', '玉屏侗族自治县', '石阡县', '思南县', '印江土家族苗族自治县', '德江县', '沿河土家族自治县', '松桃苗族自治县'],
  '黔西南布依族苗族自治州': ['兴义市', '兴仁市', '普安县', '晴隆县', '贞丰县', '望谟县', '册亨县', '安龙县'],
  '黔东南苗族侗族自治州': ['凯里市', '黄平县', '施秉县', '三穗县', '镇远县', '岑巩县', '天柱县', '锦屏县', '剑河县', '台江县', '黎平县', '榕江县', '从江县', '雷山县', '麻江县', '丹寨县'],
  '黔南布依族苗族自治州': ['都匀市', '福泉市', '荔波县', '贵定县', '瓮安县', '独山县', '平塘县', '罗甸县', '长顺县', '龙里县', '惠水县', '三都水族自治县'],
  // 云南省
  '昆明市': ['五华区', '盘龙区', '官渡区', '西山区', '东川区', '呈贡区', '晋宁区', '富民县', '宜良县', '石林彝族自治县', '嵩明县', '禄劝彝族苗族自治县', '寻甸回族彝族自治县', '安宁市'],
  '曲靖市': ['麒麟区', '沾益区', '马龙区', '陆良县', '师宗县', '罗平县', '富源县', '会泽县', '宣威市'],
  '玉溪市': ['红塔区', '江川区', '通海县', '华宁县', '易门县', '峨山彝族自治县', '新平彝族傣族自治县', '元江哈尼族彝族傣族自治县', '澄江市'],
  '保山市': ['隆阳区', '施甸县', '龙陵县', '昌宁县', '腾冲市'],
  '昭通市': ['昭阳区', '鲁甸县', '巧家县', '盐津县', '大关县', '永善县', '绥江县', '镇雄县', '彝良县', '威信县', '水富市'],
  '丽江市': ['古城区', '玉龙纳西族自治县', '永胜县', '华坪县', '宁蒗彝族自治县'],
  '普洱市': ['思茅区', '宁洱哈尼族彝族自治县', '墨江哈尼族自治县', '景东彝族自治县', '景谷傣族彝族自治县', '镇沅彝族哈尼族拉祜族自治县', '江城哈尼族彝族自治县', '孟连傣族拉祜族佤族自治县', '澜沧拉祜族自治县', '西盟佤族自治县'],
  '临沧市': ['临翔区', '凤庆县', '云县', '永德县', '镇康县', '双江拉祜族佤族布朗族傣族自治县', '耿马傣族佤族自治县', '沧源佤族自治县'],
  '楚雄彝族自治州': ['楚雄市', '禄丰市', '双柏县', '牟定县', '南华县', '姚安县', '大姚县', '永仁县', '元谋县', '武定县'],
  '红河哈尼族彝族自治州': ['个旧市', '开远市', '蒙自市', '弥勒市', '屏边苗族自治县', '建水县', '石屏县', '泸西县', '元阳县', '红河县', '金平苗族瑶族傣族自治县', '绿春县', '河口瑶族自治县'],
  '文山壮族苗族自治州': ['文山市', '砚山县', '西畴县', '麻栗坡县', '马关县', '丘北县', '广南县', '富宁县'],
  '西双版纳傣族自治州': ['景洪市', '勐海县', '勐腊县'],
  '大理白族自治州': ['大理市', '漾濞彝族自治县', '祥云县', '宾川县', '弥渡县', '南涧彝族自治县', '巍山彝族回族自治县', '永平县', '云龙县', '洱源县', '剑川县', '鹤庆县'],
  '德宏傣族景颇族自治州': ['瑞丽市', '芒市', '梁河县', '盈江县', '陇川县'],
  '怒江傈僳族自治州': ['泸水市', '福贡县', '贡山独龙族怒族自治县', '兰坪白族普米族自治县'],
  '迪庆藏族自治州': ['香格里拉市', '德钦县', '维西傈僳族自治县'],
  // 陕西省
  '西安市': ['新城区', '碑林区', '莲湖区', '灞桥区', '未央区', '雁塔区', '阎良区', '临潼区', '长安区', '高陵区', '鄠邑区', '蓝田县', '周至县'],
  '铜川市': ['王益区', '印台区', '耀州区', '宜君县'],
  '宝鸡市': ['渭滨区', '金台区', '陈仓区', '凤翔区', '岐山县', '扶风县', '眉县', '陇县', '千阳县', '麟游县', '凤县', '太白县'],
  '咸阳市': ['秦都区', '杨陵区', '渭城区', '三原县', '泾阳县', '乾县', '礼泉县', '永寿县', '彬县', '旬邑县', '淳化县', '武功县', '兴平市', '彬州市'],
  '渭南市': ['临渭区', '华州区', '潼关县', '大荔县', '合阳县', '澄城县', '蒲城县', '白水县', '富平县', '韩城市', '华阴市'],
  '延安市': ['宝塔区', '安塞区', '延长县', '延川县', '志丹县', '吴起县', '甘泉县', '富县', '洛川县', '宜川县', '黄龙县', '黄陵县', '子长市'],
  '汉中市': ['汉台区', '南郑区', '城固区', '洋县', '西乡县', '勉县', '宁强县', '略阳县', '镇巴县', '留坝县', '佛坪县'],
  '榆林市': ['榆阳区', '横山区', '府谷县', '靖边县', '定边县', '绥德县', '米脂县', '佳县', '吴堡县', '清涧县', '子洲县', '神木市'],
  '安康市': ['汉滨区', '汉阴县', '石泉县', '宁陕县', '紫阳县', '岚皋县', '平利县', '镇坪县', '旬阳市', '白河县'],
  '商洛市': ['商州区', '洛南县', '丹凤县', '商南县', '山阳县', '镇安县', '柞水县'],
  // 甘肃省
  '兰州市': ['城关区', '七里河区', '西固区', '安宁区', '红古区', '永登县', '皋兰县', '榆中县'],
  '嘉峪关市': ['雄关街道', '钢城街道', '新城镇', '峪泉镇', '文殊镇'],
  '金昌市': ['金川区', '永昌县'],
  '白银市': ['白银区', '平川区', '靖远县', '会宁县', '景泰县'],
  '天水市': ['秦州区', '麦积区', '清水县', '秦安县', '甘谷县', '武山县', '张家川回族自治县'],
  '武威市': ['凉州区', '民勤县', '古浪县', '天祝藏族自治县'],
  '张掖市': ['甘州区', '肃南裕固族自治县', '民乐县', '临泽县', '高台县', '山丹县'],
  '平凉市': ['崆峒区', '泾川县', '灵台县', '崇信县', '庄浪县', '静宁县', '华亭市'],
  '酒泉市': ['肃州区', '金塔县', '瓜州县', '肃北蒙古族自治县', '阿克塞哈萨克族自治县', '玉门市', '敦煌市'],
  '庆阳市': ['西峰区', '庆城县', '环县', '华池县', '合水县', '正宁县', '宁县', '镇原县'],
  '定西市': ['安定区', '通渭县', '陇西县', '渭源县', '临洮县', '漳县', '岷县'],
  '陇南市': ['武都区', '成县', '文县', '宕昌县', '康县', '西和县', '礼县', '徽县', '两当县'],
  '临夏回族自治州': ['临夏市', '临夏县', '康乐县', '永靖县', '广河县', '和政县', '东乡族自治县', '积石山保安族东乡族撒拉族自治县'],
  '甘南藏族自治州': ['合作市', '临潭县', '卓尼县', '舟曲县', '迭部县', '玛曲县', '碌曲县', '夏河县'],
  // 青海省
  '西宁市': ['城东区', '城中区', '城西区', '城北区', '湟中区', '大通回族土族自治县', '湟源县'],
  '海东市': ['乐都区', '平安区', '民和回族土族自治县', '互助土族自治县', '化隆回族自治县', '循化撒拉族自治县'],
  '海北藏族自治州': ['门源回族自治县', '祁连县', '海晏县', '刚察县'],
  '黄南藏族自治州': ['同仁市', '尖扎县', '泽库县', '河南蒙古族自治县'],
  '海南藏族自治州': ['共和县', '同德县', '贵德县', '兴海县', '贵南县'],
  '果洛藏族自治州': ['玛沁县', '班玛县', '甘德县', '达日县', '久治县', '玛多县'],
  '玉树藏族自治州': ['玉树市', '杂多县', '称多县', '治多县', '囊谦县', '曲麻莱县'],
  '海西蒙古族藏族自治州': ['格尔木市', '德令哈市', '茫崖市', '乌兰县', '都兰县', '天峻县', '大柴旦行政委员会'],
  // 台湾省
  '台北市': ['中正区', '大同区', '中山区', '松山区', '大安区', '万华区', '信义区', '士林区', '北投区', '内湖区', '南港区', '文山区'],
  '新北市': ['板桥区', '三重区', '中和区', '永和区', '新庄区', '新店区', '树林区', '莺歌区', '三峡区', '淡水区', '汐止区', '瑞芳区', '土城区', '芦洲区', '五股区', '泰山区', '林口区', '深坑区', '石碇区', '坪林区', '三芝区', '石门区', '八里区', '平溪区', '双溪区', '贡寮区', '金山区', '万里区', '乌来区'],
  '桃园市': ['桃园区', '中坜区', '平镇区', '八德区', '杨梅区', '芦竹区', '大溪区', '龙潭区', '龟山区', '大园区', '观音区', '新屋区', '复兴区'],
  '台中市': ['中区', '东区', '南区', '西区', '北区', '北屯区', '西屯区', '南屯区', '太平区', '大里区', '雾峰区', '乌日区', '丰原区', '后里区', '石冈区', '东势区', '和平区', '新社区', '潭子区', '大雅区', '神冈区', '大肚区', '沙鹿区', '大社区', '仁武区', '鸟松区', '冈山区', '桥头区', '燕巢区', '田寮区', '金山区', '万里区', '乌来区'],
  '台南市': ['中西区', '东区', '南区', '北区', '安平区', '安南区', '永康区', '归仁区', '新化区', '左镇区', '玉井区', '楠西区', '南化区', '仁德区', '关庙区', '龙崎区', '官田区', '麻豆区', '佳里区', '西港区', '七股区', '将军区', '学甲区', '北门区', '新营区', '后壁区', '白河区', '东山区', '六甲区', '下营区', '柳营区', '盐水区', '善化区', '大内区', '山上区', '新市区', '安定区'],
  '高雄市': ['盐埕区', '鼓山区', '左营区', '楠梓区', '三民区', '新兴区', '前金区', '苓雅区', '前镇区', '旗津区', '小港区', '凤山区', '林园区', '大寮区', '大树区', '大社区', '仁武区', '鸟松区', '冈山区', '桥头区', '燕巢区', '田寮区', '金山区', '万里区', '乌来区'],
  // 内蒙古自治区
  '呼和浩特市': ['回民区', '玉泉区', '赛罕区', '新城区', '土默特左旗', '托克托县', '和林格尔县', '清水河县', '武川县'],
  '包头市': ['东河区', '昆都仑区', '青山区', '石拐区', '九原区', '白云鄂博矿区', '土默特右旗', '固阳县', '达尔罕茂明安联合旗'],
  '乌海市': ['海勃湾区', '海南区', '乌达区'],
  '赤峰市': ['红山区', '元宝山区', '松山区', '阿鲁科尔沁旗', '巴林左旗', '巴林右旗', '林西县', '克什克腾旗', '翁牛特旗', '喀喇沁旗', '宁城县', '敖汉旗'],
  '通辽市': ['科尔沁区', '科尔沁左翼中旗', '科尔沁左翼后旗', '开鲁县', '库伦旗', '奈曼旗', '扎鲁特旗', '霍林郭勒市'],
  '鄂尔多斯市': ['东胜区', '康巴什区', '达拉特旗', '准格尔旗', '鄂托克前旗', '鄂托克旗', '杭锦旗', '乌审旗', '伊金霍洛旗'],
  '呼伦贝尔市': ['海拉尔区', '扎赉诺尔区', '阿荣旗', '莫力达瓦达斡尔族自治旗', '鄂伦春自治旗', '鄂温克族自治旗', '陈巴尔虎旗', '新巴尔虎左旗', '新巴尔虎右旗', '满洲里市', '牙克石市', '扎兰屯市', '额尔古纳市', '根河市'],
  '巴彦淖尔市': ['临河区', '五原县', '磴口县', '乌拉特前旗', '乌拉特中旗', '乌拉特后旗', '杭锦后旗'],
  '乌兰察布市': ['集宁区', '卓资县', '化德县', '商都县', '兴和县', '凉城县', '察哈尔右翼前旗', '察哈尔右翼中旗', '察哈尔右翼后旗', '四子王旗', '丰镇市'],
  '兴安盟': ['乌兰浩特市', '阿尔山市', '科尔沁右翼前旗', '科尔沁右翼中旗', '扎赉特旗', '突泉县'],
  '锡林郭勒盟': ['二连浩特市', '锡林浩特市', '阿巴嘎旗', '苏尼特左旗', '苏尼特右旗', '东乌珠穆沁旗', '西乌珠穆沁旗', '太仆寺旗', '镶黄旗', '正镶白旗', '正蓝旗', '多伦县'],
  '阿拉善盟': ['阿拉善左旗', '阿拉善右旗', '额济纳旗'],
  // 广西壮族自治区
  '南宁市': ['兴宁区', '青秀区', '江南区', '西乡塘区', '良庆区', '邕宁区', '武鸣区', '隆安县', '马山县', '上林县', '宾阳县', '横州市'],
  '柳州市': ['城中区', '鱼峰区', '柳南区', '柳北区', '柳江区', '柳城县', '鹿寨县', '融安县', '融水苗族自治县', '三江侗族自治县'],
  '桂林市': ['秀峰区', '叠彩区', '象山区', '七星区', '雁山区', '临桂区', '阳朔县', '灵川县', '全州县', '兴安县', '永福县', '灌阳县', '龙胜各族自治县', '资源县', '平乐县', '恭城瑶族自治县', '荔浦市'],
  '梧州市': ['万秀区', '长洲区', '龙圩区', '苍梧县', '藤县', '蒙山县', '岑溪市'],
  '北海市': ['海城区', '银海区', '铁山港区', '合浦县'],
  '防城港市': ['港口区', '防城区', '上思县', '东兴市'],
  '钦州市': ['钦南区', '钦北区', '灵山县', '浦北县'],
  '贵港市': ['港北区', '港南区', '覃塘区', '平南县', '桂平市'],
  '玉林市': ['玉州区', '福绵区', '容县', '陆川县', '博白县', '兴业县', '北流市'],
  '百色市': ['右江区', '田阳区', '田东县', '德保县', '那坡县', '凌云县', '乐业县', '田林县', '西林县', '隆林各族自治县', '靖西市', '平果市'],
  '贺州市': ['八步区', '平桂区', '昭平县', '钟山县', '富川瑶族自治县'],
  '河池市': ['金城江区', '宜州区', '南丹县', '天峨县', '凤山县', '东兰县', '罗城仫佬族自治县', '环江毛南族自治县', '巴马瑶族自治县', '都安瑶族自治县', '大化瑶族自治县'],
  '来宾市': ['兴宾区', '忻城县', '象州县', '武宣县', '金秀瑶族自治县', '合山市'],
  '崇左市': ['江州区', '扶绥县', '宁明县', '龙州县', '大新县', '天等县', '凭祥市'],
  // 西藏自治区
  '拉萨市': ['城关区', '堆龙德庆区', '达孜区', '林周县', '当雄县', '尼木县', '曲水县', '墨竹工卡县'],
  '日喀则市': ['桑珠孜区', '南木林县', '江孜县', '定日县', '萨迦县', '拉孜县', '昂仁县', '谢通门县', '白朗县', '仁布县', '康马县', '定结县', '仲巴县', '亚东县', '吉隆县', '聂拉木县', '萨嘎县', '岗巴县'],
  '昌都市': ['卡若区', '江达县', '贡觉县', '类乌齐县', '丁青县', '察雅县', '八宿县', '左贡县', '芒康县', '洛隆县', '边坝县'],
  '林芝市': ['巴宜区', '工布江达县', '米林市', '墨脱县', '波密县', '察隅县', '朗县'],
  '山南市': ['乃东区', '扎囊县', '贡嘎县', '桑日县', '琼结县', '曲松县', '措美县', '洛扎县', '加查县', '隆子县', '错那市', '浪卡子县'],
  '那曲市': ['色尼区', '嘉黎县', '比如县', '聂荣县', '安多县', '申扎县', '索县', '班戈县', '巴青县', '尼玛县', '双湖县'],
  '阿里地区': ['普兰县', '札达县', '噶尔县', '日土县', '革吉县', '改则县', '措勤县'],
  // 宁夏回族自治区
  '银川市': ['兴庆区', '西夏区', '金凤区', '永宁县', '贺兰县', '灵武市'],
  '石嘴山市': ['大武口区', '惠农区', '平罗县'],
  '吴忠市': ['利通区', '红寺堡区', '盐池县', '同心县', '青铜峡市'],
  '固原市': ['原州区', '西吉县', '隆德县', '泾源县', '彭阳县'],
  '中卫市': ['沙坡头区', '中宁县', '海原县'],
  // 新疆维吾尔自治区
  '乌鲁木齐市': ['天山区', '沙依巴克区', '新市区', '水磨沟区', '头屯河区', '达坂城区', '米东区', '乌鲁木齐县'],
  '克拉玛依市': ['独山子区', '克拉玛依区', '白碱滩区', '乌尔禾区'],
  '吐鲁番市': ['高昌区', '鄯善县', '托克逊县'],
  '哈密市': ['伊州区', '巴里坤哈萨克自治县', '伊吾县'],
  '昌吉回族自治州': ['昌吉市', '阜康市', '呼图壁县', '玛纳斯县', '奇台县', '吉木萨尔县', '木垒哈萨克自治县'],
  '博尔塔拉蒙古自治州': ['博乐市', '阿拉山口市', '精河县', '温泉县'],
  '巴音郭楞蒙古自治州': ['库尔勒市', '轮台县', '尉犁县', '若羌县', '且末县', '焉耆回族自治县', '和静县', '和硕县', '博湖县'],
  '阿克苏地区': ['阿克苏市', '库车市', '温宿县', '沙雅县', '新和县', '拜城县', '乌什县', '阿瓦提县', '柯坪县'],
  '克孜勒苏柯尔克孜自治州': ['阿图什市', '阿克陶县', '阿合奇县', '乌恰县'],
  '喀什地区': ['喀什市', '疏附县', '疏勒县', '英吉沙县', '泽普县', '莎车县', '叶城县', '麦盖提县', '岳普湖县', '伽师县', '巴楚县', '塔什库尔干塔吉克自治县'],
  '和田地区': ['和田市', '和田县', '墨玉县', '皮山县', '洛浦县', '策勒县', '于田县', '民丰县'],
  '伊犁哈萨克自治州': ['伊宁市', '奎屯市', '霍尔果斯市', '伊宁县', '察布查尔锡伯自治县', '霍城县', '巩留县', '新源县', '昭苏县', '特克斯县', '尼勒克县'],
  '塔城地区': ['塔城市', '乌苏市', '沙湾市', '额敏县', '托里县', '裕民县', '和布克赛尔蒙古自治县'],
  '阿勒泰地区': ['阿勒泰市', '布尔津县', '富蕴县', '福海县', '哈巴河县', '青河县', '吉木乃县'],
};

interface DbAddress {
  id: string;
  name: string;
  phone: string;
  province: string;
  city: string;
  district: string;
  detail: string;
  is_default: boolean;
}

interface CartItem {
  id: string;
  product_id: string;
  quantity: number;
  selected: boolean;
  products: {
    id: string;
    name: string;
    price: number;
    images: string[] | null;
  } | null;
}

// ─── 等级配置（与 mine 页面保持一致）───────────────────────────────────────
const LEVEL_CONFIG: Record<string, {
  icon: string;
  topBar: string;
  bg: string;
  iconBg: string;
  iconColor: string;
  accentColor: string;
  badgeBg: string;
  badgeText: string;
  btnGradient: string;
  direct: string;
  reward: string;
  perks: Array<{ icon: string; text: string }>;
}> = {
  '会员': {
    icon: 'ri-vip-crown-2-fill',
    topBar: 'from-gray-400 via-gray-300 to-gray-400',
    bg: 'from-gray-50 to-white',
    iconBg: 'from-gray-100 to-gray-50',
    iconColor: 'text-gray-500',
    accentColor: 'text-gray-600',
    badgeBg: 'bg-gray-100',
    badgeText: 'text-gray-600',
    btnGradient: 'from-gray-500 to-gray-400',
    direct: '20%',
    reward: '60/180',
    perks: [
      { icon: 'ri-percent-line', text: '直推返佣 20%' },
      { icon: 'ri-money-cny-circle-line', text: '销售奖励 60/180' },
    ],
  },
  '销售员': {
    icon: 'ri-shield-star-fill',
    topBar: 'from-emerald-400 via-teal-300 to-emerald-400',
    bg: 'from-emerald-50 to-white',
    iconBg: 'from-emerald-100 to-teal-50',
    iconColor: 'text-emerald-500',
    accentColor: 'text-emerald-600',
    badgeBg: 'bg-emerald-50',
    badgeText: 'text-emerald-600',
    btnGradient: 'from-emerald-500 to-teal-400',
    direct: '25%',
    reward: '75/225',
    perks: [
      { icon: 'ri-percent-line', text: '直推返佣 25%' },
      { icon: 'ri-money-cny-circle-line', text: '销售奖励 75/225' },
    ],
  },
  '批发': {
    icon: 'ri-price-tag-3-fill',
    topBar: 'from-sky-400 via-cyan-300 to-sky-400',
    bg: 'from-sky-50 to-white',
    iconBg: 'from-sky-100 to-cyan-50',
    iconColor: 'text-sky-500',
    accentColor: 'text-sky-600',
    badgeBg: 'bg-sky-50',
    badgeText: 'text-sky-600',
    btnGradient: 'from-sky-500 to-cyan-400',
    direct: '30%',
    reward: '90/270',
    perks: [
      { icon: 'ri-percent-line', text: '直推返佣 30%' },
      { icon: 'ri-money-cny-circle-line', text: '销售奖励 90/270' },
    ],
  },
  '主管': {
    icon: 'ri-award-fill',
    topBar: 'from-amber-400 via-yellow-300 to-amber-400',
    bg: 'from-amber-50 to-white',
    iconBg: 'from-amber-100 to-yellow-50',
    iconColor: 'text-amber-500',
    accentColor: 'text-amber-600',
    badgeBg: 'bg-amber-50',
    badgeText: 'text-amber-600',
    btnGradient: 'from-amber-500 to-yellow-400',
    direct: '35%',
    reward: '105/315',
    perks: [
      { icon: 'ri-percent-line', text: '直推返佣 35%' },
      { icon: 'ri-money-cny-circle-line', text: '销售奖励 105/315' },
    ],
  },
  '经理': {
    icon: 'ri-vip-crown-fill',
    topBar: 'from-orange-400 via-amber-300 to-orange-400',
    bg: 'from-orange-50 to-white',
    iconBg: 'from-orange-100 to-amber-50',
    iconColor: 'text-orange-500',
    accentColor: 'text-orange-600',
    badgeBg: 'bg-orange-50',
    badgeText: 'text-orange-600',
    btnGradient: 'from-orange-500 to-amber-400',
    direct: '40%',
    reward: '120/360',
    perks: [
      { icon: 'ri-percent-line', text: '直推返佣 40%' },
      { icon: 'ri-money-cny-circle-line', text: '销售奖励 120/360' },
    ],
  },
  '总监': {
    icon: 'ri-medal-fill',
    topBar: 'from-rose-400 via-pink-300 to-rose-400',
    bg: 'from-rose-50 to-white',
    iconBg: 'from-rose-100 to-pink-50',
    iconColor: 'text-rose-500',
    accentColor: 'text-rose-600',
    badgeBg: 'bg-rose-50',
    badgeText: 'text-rose-600',
    btnGradient: 'from-rose-500 to-pink-400',
    direct: '45%',
    reward: '135/405',
    perks: [
      { icon: 'ri-percent-line', text: '直推返佣 45%' },
      { icon: 'ri-money-cny-circle-line', text: '销售奖励 135/405' },
    ],
  },
  '董事': {
    icon: 'ri-vip-diamond-fill',
    topBar: 'from-violet-400 via-purple-300 to-violet-400',
    bg: 'from-violet-50 to-white',
    iconBg: 'from-violet-100 to-purple-50',
    iconColor: 'text-violet-500',
    accentColor: 'text-violet-600',
    badgeBg: 'bg-violet-50',
    badgeText: 'text-violet-600',
    btnGradient: 'from-violet-500 to-purple-400',
    direct: '50%',
    reward: '150/450',
    perks: [
      { icon: 'ri-percent-line', text: '直推返佣 50%' },
      { icon: 'ri-money-cny-circle-line', text: '销售奖励 150/450' },
    ],
  },
  '总裁': {
    icon: 'ri-vip-crown-2-fill',
    topBar: 'from-red-500 via-rose-400 to-red-500',
    bg: 'from-red-50 to-white',
    iconBg: 'from-red-100 to-rose-50',
    iconColor: 'text-red-500',
    accentColor: 'text-red-600',
    badgeBg: 'bg-red-50',
    badgeText: 'text-red-600',
    btnGradient: 'from-red-500 to-rose-400',
    direct: '55%',
    reward: '165/495',
    perks: [
      { icon: 'ri-percent-line', text: '直推返佣 55%' },
      { icon: 'ri-money-cny-circle-line', text: '销售奖励 165/495' },
      { icon: 'ri-star-fill', text: '最高荣誉 · 总裁级别' },
    ],
  },
};

// 升级庆祝弹窗（通用，根据等级动态变色）
const LevelUpModal = ({ levelName, onClose }: { levelName: string; onClose: () => void }) => {
  const cfg = LEVEL_CONFIG[levelName] ?? LEVEL_CONFIG['会员'];
  const isTopLevel = levelName === '总裁';

  return (
    <>
      <div className="fixed inset-0 bg-black/60 z-[60]" />
      <div className="fixed inset-0 flex items-center justify-center z-[60] px-6">
        <div
          className={`bg-gradient-to-b ${cfg.bg} rounded-3xl w-full flex flex-col items-center relative overflow-hidden`}
          style={{ animation: 'popIn 0.5s cubic-bezier(0.34,1.56,0.64,1)' }}
        >
          {/* 顶部彩条 */}
          <div className={`absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r ${cfg.topBar}`} />

          {/* 背景光晕 */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-48 h-48 rounded-full opacity-20 pointer-events-none"
            style={{ background: `radial-gradient(circle, currentColor, transparent)` }} />

          <div className="relative z-10 flex flex-col items-center px-7 pt-8 pb-7 w-full">
            {/* 图标 */}
            <div
              className={`w-24 h-24 flex items-center justify-center bg-gradient-to-br ${cfg.iconBg} rounded-full mb-4 border-4 border-white shadow-lg`}
              style={{ animation: 'scaleIn 0.6s cubic-bezier(0.34,1.56,0.64,1) 0.1s both' }}
            >
              <i className={`${cfg.icon} ${cfg.iconColor} text-5xl`} style={{ animation: 'fadeIn 0.4s ease 0.4s both' }} />
            </div>

            {/* 标题 */}
            <div style={{ animation: 'fadeUp 0.4s ease 0.2s both' }} className="text-center mb-1">
              <p className={`text-xs ${cfg.accentColor} font-semibold tracking-widest mb-1`}>
                {isTopLevel ? '🎉 恭喜达到最高荣誉' : '恭喜晋级'}
              </p>
              <h3 className="font-bold text-gray-900 text-2xl">
                正式成为<span className={cfg.accentColor}>「{levelName}」</span>！
              </h3>
            </div>

            {/* 副标题 */}
            <p
              className="text-sm text-gray-500 text-center mb-5 leading-relaxed"
              style={{ animation: 'fadeUp 0.4s ease 0.3s both' }}
            >
              直推返佣提升至
              <span className={`${cfg.accentColor} font-bold text-base mx-1`}>{cfg.direct}</span>
              ，解锁更多分销权益
            </p>

            {/* 权益列表 */}
            <div
              className={`w-full ${cfg.badgeBg} rounded-2xl p-4 mb-5 space-y-2.5`}
              style={{ animation: 'fadeUp 0.4s ease 0.35s both' }}
            >
              {cfg.perks.map((item) => (
                <div key={item.text} className="flex items-center gap-2.5">
                  <div className={`w-7 h-7 flex items-center justify-center rounded-full bg-white flex-shrink-0 shadow-sm`}>
                    <i className={`${item.icon} ${cfg.accentColor} text-sm`} />
                  </div>
                  <span className="text-sm text-gray-700 font-medium">{item.text}</span>
                </div>
              ))}
            </div>

            {/* 按钮 */}
            <button
              onClick={onClose}
              className={`w-full bg-gradient-to-r ${cfg.btnGradient} text-white py-3.5 rounded-full text-sm font-bold cursor-pointer whitespace-nowrap shadow-md`}
              style={{ animation: 'fadeUp 0.4s ease 0.45s both' }}
            >
              {isTopLevel ? '荣耀加身，查看订单' : '太棒了，查看订单'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────────

const CheckoutPage = () => {
  const navigate = useNavigate();
  const { user, refresh: refreshAuth } = useAuth();

  const [showSuccess, setShowSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [note, setNote] = useState('');
  const [orderId, setOrderId] = useState('');
  const [finalRemainPoints, setFinalRemainPoints] = useState(0);
  const [finalTotalPoints, setFinalTotalPoints] = useState(0);

  // 升级弹窗
  const [showLevelUp, setShowLevelUp] = useState(false);
  const [upgradedLevel, setUpgradedLevel] = useState('');

  // 支付密码弹窗
  const [showPayModal, setShowPayModal] = useState(false);
  const [payPwdError, setPayPwdError] = useState('');
  const [payPwdLoading, setPayPwdLoading] = useState(false);

  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [loadingCart, setLoadingCart] = useState(true);

  const [addresses, setAddresses] = useState<DbAddress[]>([]);
  const [selectedAddrId, setSelectedAddrId] = useState<string | null>(null);
  const [loadingAddr, setLoadingAddr] = useState(false);
  const [showAddrSheet, setShowAddrSheet] = useState(false);
  const [showAddrForm, setShowAddrForm] = useState(false);

  const [addrForm, setAddrForm] = useState({ name: '', phone: '', province: '', city: '', district: '', detail: '', is_default: false });
  const [addrFormErr, setAddrFormErr] = useState('');
  const [savingAddr, setSavingAddr] = useState(false);

  // 获取地址列表
  const fetchAddresses = useCallback(async () => {
    if (!user) return;
    setLoadingAddr(true);
    const { data } = await supabase
      .from('addresses')
      .select('*')
      .eq('user_id', user.id)
      .order('is_default', { ascending: false })
      .order('created_at', { ascending: false });
    const list = (data as DbAddress[]) ?? [];
    setAddresses(list);
    // 自动选中默认地址
    const def = list.find((a) => a.is_default) ?? list[0];
    if (def) setSelectedAddrId(def.id);
    setLoadingAddr(false);
  }, [user]);

  useEffect(() => { fetchAddresses(); }, [fetchAddresses]);

  // 获取购物车
  const fetchCart = useCallback(async () => {
    if (!user) {
      setLoadingCart(false);
      return;
    }
    setLoadingCart(true);
    const { data, error } = await supabase
      .from('cart_items')
      .select('*, products(id, name, price, images)')
      .eq('user_id', user.id)
      .eq('selected', true);
    if (error) {
      console.error('[checkout] fetchCart error:', error);
    }
    if (!error && data) setCartItems(data as CartItem[]);
    setLoadingCart(false);
  }, [user]);

  useEffect(() => { fetchCart(); }, [fetchCart]);

  const checkoutItems = cartItems;
  const totalPoints = checkoutItems.reduce((sum, item) => sum + (item.products?.price || 0) * item.quantity, 0);
  // 直接从DB读取最新积分，监听整个 user 对象
  const [userPoints, setUserPoints] = useState<number | null>(null); // null = 未加载
  const [pointsLoading, setPointsLoading] = useState(true);
  useEffect(() => {
    if (!user?.id) { setPointsLoading(false); return; }
    setPointsLoading(true);
    supabase.from('custom_users').select('points').eq('id', user.id).maybeSingle()
      .then(({ data }) => {
        if (data != null) setUserPoints(data.points);
        setPointsLoading(false);
      });
  }, [user]);
  const pointsLoaded = userPoints !== null;
  const canExchange = pointsLoaded && (userPoints as number) >= totalPoints && checkoutItems.length > 0;
  const selectedAddr = addresses.find((a) => a.id === selectedAddrId) ?? addresses[0] ?? null;
  // 是否可以点击提交：积分足够 + 有商品 + 有地址
  const canSubmit = canExchange && !!selectedAddr && !submitting && pointsLoaded;

  const openAddForm = () => {
    setAddrForm({ name: '', phone: '', province: '', city: '', district: '', detail: '', is_default: false });
    setAddrFormErr('');
    setShowAddrForm(true);
  };

  // 根据省份获取城市列表
  const currentProvince = useMemo(() => PROVINCES.find(p => p.name === addrForm.province), [addrForm.province]);
  const currentCity = addrForm.city;
  const districts = useMemo(() => DISTRICTS[currentCity] ?? [], [currentCity]);

  const handleSaveAddr = async () => {
    if (!addrForm.name.trim()) { setAddrFormErr('请输入收货人姓名'); return; }
    if (!addrForm.phone.trim()) { setAddrFormErr('请输入手机号'); return; }
    if (!addrForm.province) { setAddrFormErr('请选择省份'); return; }
    if (!addrForm.city) { setAddrFormErr('请选择城市'); return; }
    if (!addrForm.district) { setAddrFormErr('请选择区/县'); return; }
    if (!addrForm.detail.trim()) { setAddrFormErr('请输入详细地址'); return; }
    if (!user) return;
    setSavingAddr(true);
    const { data, error } = await supabase.from('addresses').insert({
      user_id: user.id,
      name: addrForm.name.trim(),
      phone: addrForm.phone.trim(),
      province: addrForm.province,
      city: addrForm.city,
      district: addrForm.district,
      detail: addrForm.detail.trim(),
      is_default: addrForm.is_default,
    }).select().maybeSingle();
    setSavingAddr(false);
    if (error) { setAddrFormErr('保存失败，请重试'); return; }
    await fetchAddresses();
    if (data) setSelectedAddrId((data as DbAddress).id);
    setShowAddrForm(false);
    setShowAddrSheet(false);
  };

  // 点击"确认兑换"按钮 → 必须有地址 + 积分足够 → 弹出支付密码框
  const handleClickSubmit = () => {
    // 积分未加载完，不能提交
    if (!pointsLoaded || userPoints === null) {
      setSubmitError('积分信息加载中，请稍候...');
      return;
    }
    // 没有选中地址
    if (!selectedAddr) {
      setSubmitError('请先添加收货地址');
      setShowAddrSheet(true);
      return;
    }
    // 积分不足
    if ((userPoints as number) < totalPoints) {
      setSubmitError(`积分不足，还差 ${(totalPoints - (userPoints as number)).toLocaleString()} 积分`);
      return;
    }
    // 购物车为空
    if (checkoutItems.length === 0) {
      setSubmitError('购物车为空');
      return;
    }
    setSubmitError('');
    setPayPwdError('');
    setShowPayModal(true);
  };

  // 支付密码验证 + 下单
  const handlePayPasswordConfirm = async (password: string) => {
    if (!user) return;
    // 防止重复提交
    if (submitting) return;
    setPayPwdLoading(true);
    setPayPwdError('');

    // 验证支付密码（从数据库查询 pay_password 字段）
    const { data: userData, error: queryError } = await supabase
      .from('custom_users')
      .select('pay_password')
      .eq('id', user.id)
      .maybeSingle();

    if (queryError || !userData) {
      setPayPwdError('验证失败，请重试');
      setPayPwdLoading(false);
      return;
    }

    if (!userData.pay_password) {
      setPayPwdError('支付密码未设置');
      setPayPwdLoading(false);
      return;
    }

    // 调试：打印 hash 对比信息
    const newHash = await hashPassword(password);
    const oldHash = await hashPasswordNoSalt(password);
    console.log('[支付密码调试] 输入密码:', password);
    console.log('[支付密码调试] 数据库存储:', userData.pay_password);
    console.log('[支付密码调试] 前端新格式hash:', newHash);
    console.log('[支付密码调试] 前端旧格式hash:', oldHash);
    console.log('[支付密码调试] 新格式匹配:', newHash === userData.pay_password);
    console.log('[支付密码调试] 旧格式匹配:', oldHash === userData.pay_password);

    const { valid: pwdValid, isLegacy } = await verifyPassword(password, userData.pay_password);
    if (!pwdValid) {
      setPayPwdError('支付密码错误，请重新输入');
      setPayPwdLoading(false);
      return;
    }

    // 如果是旧格式密码，自动迁移为新格式
    if (isLegacy) {
      const newHash = await hashPassword(password);
      await supabase.from('custom_users').update({ pay_password: newHash }).eq('id', user.id);
    }

    // 密码正确，关闭弹窗，执行下单
    setPayPwdLoading(false);
    setShowPayModal(false);
    await handleSubmit();
  };

  const handleSubmit = async () => {
    // 提交前再次校验
    if (!user || !selectedAddr || checkoutItems.length === 0) return;
    if (!pointsLoaded || (userPoints as number) < totalPoints) return;
    // 防止重复提交（双重保险）
    if (submitting) return;
    setSubmitting(true);
    setSubmitError('');

    const newOrderNo = `FZY${Date.now().toString().slice(-10)}`;

    // 1. 创建订单
    const { error: orderError } = await supabase.from('orders').insert({
      order_no: newOrderNo,
      user_id: user.id,
      items: checkoutItems.map((item) => ({
        name: item.products?.name || '商品',
        quantity: item.quantity,
        points: item.products?.price || 0,
        spec: '默认规格',
        image: item.products?.images?.[0] || '',
      })),
      total_amount: totalPoints,
      address: selectedAddr ? {
        name: selectedAddr.name,
        phone: selectedAddr.phone,
        detail: `${selectedAddr.province}${selectedAddr.city !== selectedAddr.province ? selectedAddr.city : ''}${selectedAddr.district}${selectedAddr.detail}`,
      } : null,
      status: '待发货',
    });

    if (orderError) {
      setSubmitError('下单失败，请重试');
      setSubmitting(false);
      return;
    }

    // 2. 原子扣减积分（数据库行锁，防止并发竞态和超扣）
    const { data: deductResult, error: deductError } = await supabase
      .rpc('deduct_points_atomic', {
        p_user_id: user.id,
        p_amount: totalPoints,
        p_order_no: newOrderNo,
      });

    if (deductError || !deductResult?.success) {
      // 扣减失败（积分不足或数据库错误），回滚订单
      await supabase.from('orders').delete().eq('order_no', newOrderNo);
      setSubmitError(deductResult?.error ?? '积分扣减失败，请重试');
      setSubmitting(false);
      return;
    }

    const remainAfter = deductResult.new_points;
    setUserPoints(remainAfter);

    // 写入积分消费流水
    await supabase.from('points_log').insert({
      user_id: user.id,
      delta: -totalPoints,
      type: 'expense',
      source: 'order',
      remark: `积分兑换 ${newOrderNo}`,
      balance_after: remainAfter,
    });

    // 3. 并行写入商品销量（原子 RPC 方式，避免先查后写的竞态）
    Promise.all(
      checkoutItems.map((item) => {
        if (!item.product_id) return Promise.resolve();
        return supabase.rpc('increment_product_sales', {
          p_product_id: item.product_id,
          p_quantity: item.quantity,
        }).then(({ error }) => {
          if (error) {
            return supabase
              .from('products')
              .select('sales')
              .eq('id', item.product_id)
              .maybeSingle()
              .then(({ data: prodData }) => {
                const currentSales = prodData?.sales ?? 0;
                return supabase
                  .from('products')
                  .update({ sales: currentSales + item.quantity })
                  .eq('id', item.product_id);
              });
          }
        });
      })
    ).catch((e) => console.error('销量更新失败:', e));

    // 4. 清空购物车
    await supabase
      .from('cart_items')
      .delete()
      .eq('user_id', user.id)
      .eq('selected', true);

    // 5. 分佣（含买家升级逻辑）：完全交给云函数处理
    // 云函数会：① 更新买家 performance + 判断升级 ② 给上级返佣 ③ 向上传递业绩
    // 前端不再提前修改买家 level，避免云函数读到错误的买家等级
    const userToken = localStorage.getItem('custom_auth_token') ?? '';
    const rebateRes = await fetch(
      'https://pdxhoigcrxsqwxgrphdv.supabase.co/functions/v1/custom-auth',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'process_rebate',
          buyerId: user.id,
          totalPoints,
          orderNo: newOrderNo,
          userToken,
        }),
      }
    ).then((res) => res.json()).catch((e) => { console.error('分佣请求异常:', e); return null; });

    // 从云函数返回结果中获取升级信息
    const buyerUpgraded = rebateRes?.buyerUpgraded ?? false;
    const buyerNewLevel = rebateRes?.buyerNewLevel ?? '';

    // 6. 异步刷新全局用户数据
    refreshUserFromDB().catch(() => {});
    refreshAuth();

    setFinalRemainPoints(remainAfter);
    setFinalTotalPoints(totalPoints);
    setOrderId(newOrderNo);
    setSubmitting(false);

    if (buyerUpgraded && buyerNewLevel) {
      setUpgradedLevel(buyerNewLevel);
      setShowLevelUp(true);
    } else {
      setShowSuccess(true);
    }
  };

  // 未登录提示（v2）
  if (!user && !loadingCart) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center max-w-[480px] mx-auto px-8">
        <div className="w-16 h-16 flex items-center justify-center bg-gray-100 rounded-full mb-4">
          <i className="ri-user-line text-gray-400 text-3xl" />
        </div>
        <p className="text-gray-600 font-medium mb-2">请先登录</p>
        <p className="text-gray-400 text-sm mb-6">登录后才能查看购物车和结算</p>
        <button
          onClick={() => navigate('/login')}
          className="bg-emerald-500 text-white px-8 py-3 rounded-full text-sm font-medium cursor-pointer whitespace-nowrap"
        >
          去登录
        </button>
      </div>
    );
  }

  if (showSuccess) {
    return (
      <SuccessScreen
        orderId={orderId}
        totalPoints={finalTotalPoints}
        remainPoints={finalRemainPoints}
        recipientName={selectedAddr?.name ?? ''}
        recipientPhone={selectedAddr?.phone ?? ''}
      />
    );
  }

  // ... rest of the component (addresses UI) remains the same
  return (
    <div className="min-h-screen bg-gray-50 pb-28 max-w-[480px] mx-auto">
      {/* Header */}
      <LazySection animation="fadeIn" delay={0}>
        <header className="sticky top-0 z-40 bg-white border-b border-gray-100">
          <div className="flex items-center px-4 h-14 gap-3">
            <button onClick={() => navigate(-1)} className="w-8 h-8 flex items-center justify-center cursor-pointer">
              <i className="ri-arrow-left-line text-gray-700 text-xl" />
            </button>
            <h1 className="font-bold text-gray-900 text-base">确认兑换</h1>
          </div>
        </header>
      </LazySection>

      <div className="px-4 pt-4 space-y-3">
        {/* Points Balance */}
        <LazySection animation="fadeUp" delay={60}>
          <div className="bg-gradient-to-r from-emerald-500 to-emerald-400 rounded-2xl p-4">
            <p className="text-white/70 text-xs mb-1">可用积分</p>
            <div className="flex items-baseline gap-1">
              {pointsLoading || userPoints === null ? (
                <span className="text-white/50 font-bold text-2xl animate-pulse">加载中...</span>
              ) : (
                <span className="text-white font-bold text-2xl">{userPoints.toLocaleString()}</span>
              )}
              <span className="text-white/70 text-sm">积分</span>
            </div>
            <div className="mt-2">
              {pointsLoading || userPoints === null ? (
                <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-white/20 text-white">正在获取积分信息...</span>
              ) : (
                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${canExchange ? 'bg-white/20 text-white' : 'bg-rose-400/80 text-white'}`}>
                  {canExchange ? '积分充足，可兑换' : checkoutItems.length === 0 ? '购物车为空' : `积分不足，还差 ${(totalPoints - userPoints).toLocaleString()} 积分`}
                </span>
              )}
            </div>
          </div>
        </LazySection>

        {/* Address */}
        <LazySection animation="fadeUp" delay={120}>
          <button
            onClick={() => { setShowAddrSheet(true); setShowAddrForm(false); }}
            className="w-full bg-white rounded-2xl p-4 text-left cursor-pointer active:bg-gray-50 transition-colors"
          >
            {loadingAddr ? (
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 flex items-center justify-center bg-gray-50 rounded-xl">
                  <div className="w-4 h-4 border-2 border-emerald-400 border-t-transparent rounded-full animate-spin" />
                </div>
                <span className="text-gray-400 text-sm">加载地址中...</span>
              </div>
            ) : selectedAddr ? (
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 flex items-center justify-center bg-emerald-50 rounded-xl flex-shrink-0 mt-0.5">
                  <i className="ri-map-pin-2-line text-emerald-500 text-base" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-1">
                    <span className="font-semibold text-gray-900 text-sm">{selectedAddr.name}</span>
                    <span className="text-gray-500 text-sm">{selectedAddr.phone.replace(/(\d{3})\d{4}(\d{4})/, '$1****$2')}</span>
                    {selectedAddr.is_default && <span className="text-xs bg-emerald-50 text-emerald-600 px-1.5 py-0.5 rounded">默认</span>}
                  </div>
                  <p className="text-gray-500 text-sm leading-relaxed line-clamp-2">
                    {selectedAddr.province}{selectedAddr.city !== selectedAddr.province ? selectedAddr.city : ''}{selectedAddr.district}{selectedAddr.detail}
                  </p>
                </div>
                <i className="ri-arrow-right-s-line text-gray-400 text-xl flex-shrink-0 mt-1" />
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 flex items-center justify-center bg-emerald-50 rounded-xl">
                  <i className="ri-add-line text-emerald-500 text-base" />
                </div>
                <span className="text-gray-500 text-sm">添加收货地址</span>
                <i className="ri-arrow-right-s-line text-gray-400 text-xl ml-auto" />
              </div>
            )}
          </button>
        </LazySection>

        {/* Items */}
        <LazySection animation="fadeUp" delay={180}>
          <div className="bg-white rounded-2xl p-4">
            <h3 className="font-semibold text-gray-900 text-sm mb-3">兑换商品</h3>
            {loadingCart ? (
              <div className="flex items-center justify-center py-8">
                <div className="w-6 h-6 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : checkoutItems.length === 0 ? (
              <div className="text-center py-6 text-gray-400 text-sm">
                <i className="ri-shopping-cart-line text-2xl mb-2 block" />
                购物车为空
              </div>
            ) : (
              <div className="space-y-3">
                {checkoutItems.map((item) => (
                  <div key={item.id} className="flex items-center gap-3">
                    <div className="w-14 h-14 flex-shrink-0 rounded-xl overflow-hidden bg-gray-50">
                      <img src={item.products?.images?.[0] || ''} alt={item.products?.name} className="w-full h-full object-cover object-top" loading="lazy" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-800 line-clamp-1 font-medium">{item.products?.name}</p>
                      <p className="text-xs text-gray-400 mt-0.5">默认规格</p>
                      <div className="flex items-center justify-between mt-1">
                        <div>
                          <span className="text-emerald-600 font-semibold text-sm">{(item.products?.price || 0).toLocaleString()}</span>
                          <span className="text-emerald-600 text-xs ml-0.5">积分</span>
                        </div>
                        <span className="text-gray-400 text-xs">x{item.quantity}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </LazySection>

        {/* Points Detail */}
        <LazySection animation="fadeUp" delay={240}>
          <div className="bg-white rounded-2xl p-4">
            <h3 className="font-semibold text-gray-900 text-sm mb-3">积分明细</h3>
            <div className="space-y-2.5">
              {checkoutItems.map((item) => (
                <div key={item.id} className="flex justify-between text-sm">
                  <span className="text-gray-500 line-clamp-1 flex-1 mr-2">{item.products?.name}</span>
                  <span className="text-gray-700">{((item.products?.price || 0) * item.quantity).toLocaleString()} 积分</span>
                </div>
              ))}
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">运费</span>
                <span className="text-emerald-500 text-xs">积分兑换免运费</span>
              </div>
              <div className="border-t border-gray-100 pt-2.5 flex justify-between">
                <span className="font-semibold text-gray-900 text-sm">共消耗积分</span>
                <span className="text-emerald-600 font-bold text-lg">{totalPoints.toLocaleString()} 积分</span>
              </div>
            </div>
          </div>
        </LazySection>

        {/* Note */}
        <LazySection animation="fadeUp" delay={300}>
          <div className="bg-white rounded-2xl p-4">
            <h3 className="font-semibold text-gray-900 text-sm mb-3">订单备注</h3>
            <textarea
              placeholder="选填，可以告诉我们您的特殊需求..."
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-emerald-300 resize-none"
              rows={3}
              maxLength={200}
            />
            <p className="text-xs text-gray-400 text-right mt-1">{note.length}/200</p>
          </div>
        </LazySection>
      </div>

      {/* Bottom Submit */}
      <div className="fixed bottom-0 left-0 right-0 max-w-[480px] mx-auto bg-white border-t border-gray-100 px-4 py-3 z-40">
        {submitError && (
          <p className="text-xs text-rose-500 text-center mb-2 flex items-center justify-center gap-1">
            <i className="ri-error-warning-line" />{submitError}
          </p>
        )}
        {!submitError && !loadingAddr && !selectedAddr && checkoutItems.length > 0 && (
          <p className="text-xs text-amber-500 text-center mb-2 flex items-center justify-center gap-1">
            <i className="ri-map-pin-line" />请先添加收货地址才能提交订单
          </p>
        )}
        <div className="flex items-center justify-between">
          <div>
            <span className="text-xs text-gray-500">消耗：</span>
            <span className="text-emerald-600 font-bold text-xl">{totalPoints.toLocaleString()}</span>
            <span className="text-emerald-600 text-xs ml-0.5">积分</span>
          </div>
          <button
            onClick={handleClickSubmit}
            disabled={!canSubmit}
            className={`px-8 py-3 rounded-full font-medium text-sm cursor-pointer whitespace-nowrap ${canSubmit ? 'bg-emerald-500 text-white' : 'bg-gray-200 text-gray-400'}`}
          >
            {submitting ? (
              <span className="flex items-center gap-2">
                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                提交中...
              </span>
            ) : pointsLoading || userPoints === null ? (
              '加载中...'
            ) : !selectedAddr && !loadingAddr ? (
              '请先填写地址'
            ) : userPoints < totalPoints ? (
              '积分不足'
            ) : (
              '确认兑换'
            )}
          </button>
        </div>
      </div>

      {/* 支付密码弹窗 */}
      <PayPasswordModal
        visible={showPayModal}
        totalPoints={totalPoints}
        loading={payPwdLoading}
        error={payPwdError}
        userId={user?.id ?? ''}
        onConfirm={handlePayPasswordConfirm}
        onClose={() => { setShowPayModal(false); setPayPwdError(''); }}
      />

      {/* Address Sheet */}
      {showAddrSheet && !showAddrForm && (
        <>
          <div className="fixed inset-0 bg-black/40 z-50" onClick={() => setShowAddrSheet(false)} />
          <div className="fixed bottom-0 left-0 right-0 max-w-[480px] mx-auto bg-white rounded-t-2xl z-50"
            style={{ animation: 'slideUp 0.28s cubic-bezier(0.34,1.56,0.64,1)' }}>
            <div className="flex justify-center pt-3 pb-1">
              <div className="w-10 h-1 bg-gray-200 rounded-full" />
            </div>
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-50">
              <h3 className="font-bold text-gray-900 text-base">选择收货地址</h3>
              <button onClick={() => setShowAddrSheet(false)} className="w-8 h-8 flex items-center justify-center cursor-pointer">
                <i className="ri-close-line text-gray-400 text-xl" />
              </button>
            </div>
            <div className="px-4 py-3 space-y-2 max-h-72 overflow-y-auto">
              {addresses.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <i className="ri-map-pin-line text-4xl block mb-2" />
                  <p className="text-sm">暂无收货地址</p>
                </div>
              ) : addresses.map((addr) => (
                <div
                  key={addr.id}
                  onClick={() => { setSelectedAddrId(addr.id); setShowAddrSheet(false); }}
                  className={`p-3 rounded-xl border-2 cursor-pointer transition-all ${selectedAddrId === addr.id ? 'border-emerald-400 bg-emerald-50/50' : 'border-gray-100 active:bg-gray-50'}`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-sm text-gray-900">{addr.name}</span>
                    <span className="text-gray-500 text-sm">{addr.phone}</span>
                    {addr.is_default && <span className="text-xs bg-emerald-500 text-white px-1.5 py-0.5 rounded-full">默认</span>}
                    {selectedAddrId === addr.id && <i className="ri-check-line text-emerald-500 text-sm ml-auto" />}
                  </div>
                  <p className="text-gray-500 text-xs leading-relaxed">
                    {addr.province}{addr.city !== addr.province ? addr.city : ''}{addr.district}{addr.detail}
                  </p>
                </div>
              ))}
            </div>
            <div className="px-4 pb-8 pt-2">
              <button
                onClick={() => { openAddForm(); }}
                className="w-full h-12 border-2 border-dashed border-gray-200 rounded-xl text-sm text-gray-400 flex items-center justify-center gap-2 cursor-pointer active:bg-gray-50"
              >
                <i className="ri-add-line" /> 新增收货地址
              </button>
            </div>
          </div>
        </>
      )}

      {/* Add Address Form */}
      {showAddrSheet && showAddrForm && (
        <>
          <div className="fixed inset-0 bg-black/40 z-50" onClick={() => { setShowAddrForm(false); }} />
          <div className="fixed bottom-0 left-0 right-0 max-w-[480px] mx-auto bg-white rounded-t-2xl z-50"
            style={{ animation: 'slideUp 0.28s cubic-bezier(0.34,1.56,0.64,1)' }}>
            <div className="flex justify-center pt-3 pb-1">
              <div className="w-10 h-1 bg-gray-200 rounded-full" />
            </div>
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-50">
              <button onClick={() => setShowAddrForm(false)} className="w-8 h-8 flex items-center justify-center cursor-pointer">
                <i className="ri-arrow-left-line text-gray-600 text-xl" />
              </button>
              <h3 className="font-bold text-gray-900 text-base">新增收货地址</h3>
              <div className="w-8" />
            </div>
            <div className="px-4 py-4 pb-8 overflow-y-auto max-h-[75vh] space-y-3">
              {/* 收货人 */}
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">收货人</label>
                <input
                  value={addrForm.name}
                  onChange={(e) => { setAddrForm((p) => ({ ...p, name: e.target.value })); setAddrFormErr(''); }}
                  placeholder="请输入姓名"
                  maxLength={10}
                  className="w-full h-11 px-4 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 bg-gray-50"
                />
              </div>
              {/* 手机号 */}
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">手机号</label>
                <input
                  value={addrForm.phone}
                  onChange={(e) => { setAddrForm((p) => ({ ...p, phone: e.target.value })); setAddrFormErr(''); }}
                  placeholder="请输入手机号"
                  maxLength={11}
                  inputMode="numeric"
                  className="w-full h-11 px-4 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 bg-gray-50"
                />
              </div>
              {/* 省份 */}
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">省份</label>
                <select
                  value={addrForm.province}
                  onChange={(e) => { setAddrForm((p) => ({ ...p, province: e.target.value, city: '', district: '' })); setAddrFormErr(''); }}
                  className="w-full h-11 px-4 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 bg-gray-50 appearance-none cursor-pointer"
                >
                  <option value="">请选择省份</option>
                  {PROVINCES.map((p) => (
                    <option key={p.name} value={p.name}>{p.name}</option>
                  ))}
                </select>
              </div>
              {/* 城市 */}
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">城市</label>
                <select
                  value={addrForm.city}
                  onChange={(e) => { setAddrForm((p) => ({ ...p, city: e.target.value, district: '' })); setAddrFormErr(''); }}
                  disabled={!addrForm.province}
                  className="w-full h-11 px-4 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 bg-gray-50 appearance-none cursor-pointer disabled:bg-gray-100 disabled:text-gray-400"
                >
                  <option value="">{addrForm.province ? '请选择城市' : '请先选择省份'}</option>
                  {currentProvince?.cities.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
              {/* 区/县 */}
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">区/县</label>
                <select
                  value={addrForm.district}
                  onChange={(e) => { setAddrForm((p) => ({ ...p, district: e.target.value })); setAddrFormErr(''); }}
                  disabled={!addrForm.city}
                  className="w-full h-11 px-4 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 bg-gray-50 appearance-none cursor-pointer disabled:bg-gray-100 disabled:text-gray-400"
                >
                  <option value="">{addrForm.city ? (districts.length > 0 ? '请选择区/县' : '该城市暂无区/县数据') : '请先选择城市'}</option>
                  {districts.map((d) => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
              </div>
              {/* 详细地址 */}
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">详细地址</label>
                <textarea
                  value={addrForm.detail}
                  onChange={(e) => { setAddrForm((p) => ({ ...p, detail: e.target.value })); setAddrFormErr(''); }}
                  placeholder="街道、楼栋、门牌号等"
                  maxLength={100}
                  rows={2}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 bg-gray-50 resize-none"
                />
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <div
                  onClick={() => setAddrForm((p) => ({ ...p, is_default: !p.is_default }))}
                  className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-colors ${addrForm.is_default ? 'bg-emerald-500 border-emerald-500' : 'border-gray-300'}`}
                >
                  {addrForm.is_default && <i className="ri-check-line text-white text-xs" />}
                </div>
                <span className="text-sm text-gray-600">设为默认地址</span>
              </label>
              {addrFormErr && (
                <p className="text-xs text-red-500 flex items-center gap-1">
                  <i className="ri-error-warning-line" />{addrFormErr}
                </p>
              )}
              <button
                onClick={handleSaveAddr}
                disabled={savingAddr}
                className="w-full h-12 bg-emerald-500 text-white rounded-full text-sm font-medium cursor-pointer whitespace-nowrap disabled:opacity-50"
              >
                {savingAddr ? '保存中...' : '保存地址'}
              </button>
            </div>
          </div>
        </>
      )}

      {/* 升级庆祝弹窗（全等级通用） */}
      {showLevelUp && <LevelUpModal levelName={upgradedLevel} onClose={() => { setShowLevelUp(false); setShowSuccess(true); }} />}

      <style>{`
        @keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
        @keyframes popIn { from { transform: scale(0.8); opacity: 0; } to { transform: scale(1); opacity: 1; } }
        @keyframes scaleIn { from { transform: scale(0.5); opacity: 0; } to { transform: scale(1); opacity: 1; } }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes fadeUp { from { transform: translateY(12px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
      `}</style>
    </div>
  );
};

export default CheckoutPage;